import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Navigate, useParams, useSearchParams, useLocation, Link } from "react-router-dom";
import { Collapse, Dropdown } from "react-bootstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { MdDeleteOutline, MdKeyboardArrowDown, MdAddCircle, MdSearch, MdRefresh, MdFileDownload } from "react-icons/md";
import { FiRefreshCw } from "react-icons/fi";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useDropdownReposition } from "../../hooks/useDropdownReposition";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import { useWhatsAppHeaderCheck } from "../../hooks/useWhatsAppHeaderCheck";
import {
  Utils,
  fetchWithAuth,
  getAnchoredBoundPosition,
  can,
  findOptionByValue,
  WA_AUTH_OTP_DEFAULTS,
  WA_TEMPLATE_STATUS_FILTER_OPTIONS,
  WA_TEMPLATE_CATEGORY_FILTER_OPTIONS,
  WA_TEMPLATE_BUTTON_TYPES,
  getWhatsAppTemplateStatusDisplay,
  getWhatsAppTemplateCategoryDisplay,
  formatWhatsAppMarkdownHtml,
  exportListCsv,
  templateHeaderFormat,
  isFileHeaderFormat,
  headerFileAccept,
} from "../../utils";
import WhatsAppPlatform from "./WhatsAppPlatform";
import WhatsAppMessagePreview from "./WhatsAppMessagePreview";

function templateButtons(row) {
  const comps = row && Array.isArray(row.components) ? row.components : [];
  const block = comps.find((c) => String(c.type || "").toUpperCase() === "BUTTONS");
  return Array.isArray(block?.buttons) ? block.buttons : [];
}

function templateHasDynamicUrlButton(row) {
  return templateButtons(row).some(
    (b) =>
      String(b?.type || "").toUpperCase() === "URL" && /\{\{\d+\}\}/.test(String(b.url || ""))
  );
}

function templateHasCopyCodeButton(row) {
  return templateButtons(row).some((b) => String(b?.type || "").toUpperCase() === "COPY_CODE");
}

/** Meta template header media sample — same options as WhatsApp Manager. */
const WA_HEADER_MEDIA_OPTIONS = [
  { value: "NONE", label: "None" },
  { value: "IMAGE", label: "Image" },
  { value: "VIDEO", label: "Video" },
  { value: "DOCUMENT", label: "Document" },
  // { value: "LOCATION", label: "Location" },
];

const WA_TPL_BODY_MAX = 1024;
const TPL_BTN_FIELD_HEIGHT = 38;

function wrapTextSelection(value, selectionStart, selectionEnd, open, close) {
  const from = Math.max(0, Math.min(selectionStart, selectionEnd));
  const to = Math.max(from, Math.max(selectionStart, selectionEnd));
  const selected = value.slice(from, to);
  return {
    next: `${value.slice(0, from)}${open}${selected}${close}${value.slice(to)}`,
    selStart: from + open.length,
    selEnd: from + open.length + selected.length,
  };
}

function insertTextAtSelection(value, selectionStart, selectionEnd, insert) {
  const from = Math.max(0, Math.min(selectionStart, selectionEnd));
  const to = Math.max(from, Math.max(selectionStart, selectionEnd));
  return {
    next: `${value.slice(0, from)}${insert}${value.slice(to)}`,
    selStart: from + insert.length,
    selEnd: from + insert.length,
  };
}

/** Kebab trigger for template row actions (matches User Records pattern). */
const WaTemplateRowActionsToggle = React.forwardRef(({ onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    className="dropdown-trigger-btn border-0 bg-transparent p-1"
    onClick={onClick}
    aria-label="Row actions"
  >
    <svg viewBox="0 0 16 16" fill="currentColor" width={16} height={16} aria-hidden>
      <path d="M8 5.25A1.625 1.625 0 108 2a1.625 1.625 0 000 3.25zM8 9.625a1.625 1.625 0 100-3.25 1.625 1.625 0 000 3.25zM9.625 12.375a1.625 1.625 0 11-3.25 0 1.625 1.625 0 013.25 0z" />
    </svg>
  </button>
));
WaTemplateRowActionsToggle.displayName = "WaTemplateRowActionsToggle";

const WhatsAppSettings = () => {
  const { appId, section } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const prevTemplateNameQueryRef = useRef("");
  const prevTemplateStatusQueryRef = useRef("");
  const prevTemplateCategoryQueryRef = useRef("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [account, setAccount] = useState(null);

  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [wabaId, setWabaId] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [webhookVerifyToken, setWebhookVerifyToken] = useState("");


  const [templateListLoading, setTemplateListLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [templateRows, setTemplateRows] = useState([]);
  const [templateDeletingId, setTemplateDeletingId] = useState(null);
  const [templateBulkDeleting, setTemplateBulkDeleting] = useState(false);
  /** Selected template Graph `id` values (strings) for bulk delete */
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
  const templateSelectAllRef = useRef(null);
  const templateTableScrollRef = useRef(null);
  const templateTableInnerRef = useRef(null);
  const tableMaxHeight = useTableMaxHeight(templateTableScrollRef);
  const isAtScrollEnd = useScrollEndDetection(
    templateTableScrollRef,
    templateTableInnerRef,
    templateRows.length
  );
  const templateDropdownRef = useRef(null);
  const [openTemplateActionId, setOpenTemplateActionId] = useState(null);
  const [templateDropdownPosition, setTemplateDropdownPosition] = useState(null);
  const [templateDropdownAnchorRect, setTemplateDropdownAnchorRect] = useState(null);
  const [createTplName, setCreateTplName] = useState("");
  const [createTplCategory, setCreateTplCategory] = useState("UTILITY");
  const [createTplLang, setCreateTplLang] = useState("en_US");
  const [createTplBody, setCreateTplBody] = useState("");
  const createTplBodyRef = useRef(null);
  const [createTplHeader, setCreateTplHeader] = useState("");
  const [createTplHeaderType, setCreateTplHeaderType] = useState("NONE");
  const [createTplHeaderFile, setCreateTplHeaderFile] = useState(null);
  const [createTplHeaderPreview, setCreateTplHeaderPreview] = useState("");
  const [createTplFooter, setCreateTplFooter] = useState("");
  const [createTplExample, setCreateTplExample] = useState("");
  /** Marketing/Utility CTA buttons — local type INT (`WA_TEMPLATE_BUTTON_TYPES.value`) */
  const [createTplButtons, setCreateTplButtons] = useState([]);
  /** Meta OTP auth template — defaults from WA_AUTH_OTP_DEFAULTS (editable in form) */
  const [authAddSecurityRecommendation, setAuthAddSecurityRecommendation] = useState(
    WA_AUTH_OTP_DEFAULTS.addSecurityRecommendation
  );
  const [authCodeExpirationMinutes, setAuthCodeExpirationMinutes] = useState(
    WA_AUTH_OTP_DEFAULTS.codeExpirationMinutes
  );
  const [tplCreating, setTplCreating] = useState(false);
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  /** Create modal: "custom" (build your own) or "library" (Meta pre-approved, instant). */
  const [tplCreateMode, setTplCreateMode] = useState("custom");
  const [libSearch, setLibSearch] = useState("");
  const [libCategory, setLibCategory] = useState("UTILITY");
  const [libRows, setLibRows] = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [libPick, setLibPick] = useState(null);
  const [libNewName, setLibNewName] = useState("");
  const [libButtonBaseUrl, setLibButtonBaseUrl] = useState("");
  const [libCreating, setLibCreating] = useState(false);
  /** false when Meta doesn't expose library browsing via API — manual name entry still works. */
  const [libBrowseSupported, setLibBrowseSupported] = useState(true);
  const [libManualName, setLibManualName] = useState("");
  /** AUTH code delivery — defaults from WA_AUTH_OTP_DEFAULTS */
  const [authOtpType, setAuthOtpType] = useState(WA_AUTH_OTP_DEFAULTS.otpType);
  /** Meta zero-tap terms checkbox (required when otp_type is ZERO_TAP). */
  const [authZeroTapTermsAccepted, setAuthZeroTapTermsAccepted] = useState(false);
  /** Meta "Add another app": multiple package_name + signature_hash pairs */
  const [authSupportedApps, setAuthSupportedApps] = useState([
    {
      package_name: WA_AUTH_OTP_DEFAULTS.packageName || "",
      signature_hash: WA_AUTH_OTP_DEFAULTS.signatureHash || "",
    },
  ]);
  const [authCopyButtonText, setAuthCopyButtonText] = useState(WA_AUTH_OTP_DEFAULTS.copyButtonText);
  const [authAutofillText, setAuthAutofillText] = useState(WA_AUTH_OTP_DEFAULTS.autofillText);
  const [authMessageTtlSeconds, setAuthMessageTtlSeconds] = useState(WA_AUTH_OTP_DEFAULTS.messageTtlSeconds);
  const [templateFilterAccordionOpen, setTemplateFilterAccordionOpen] = useState(false);
  /** Draft vs applied — table filters on Search (same pattern as User Records / API Keys). */
  const [draftTemplateFilterName, setDraftTemplateFilterName] = useState("");
  const [appliedTemplateFilterName, setAppliedTemplateFilterName] = useState("");
  const [draftTemplateFilterStatus, setDraftTemplateFilterStatus] = useState("");
  const [appliedTemplateFilterStatus, setAppliedTemplateFilterStatus] = useState("");
  const [draftTemplateFilterCategory, setDraftTemplateFilterCategory] = useState("");
  const [appliedTemplateFilterCategory, setAppliedTemplateFilterCategory] = useState("");

  /** Send Message audience: 'contacts' (pick/type) | 'subscribed' | 'segment' | 'test' */
  const [bulkAudience, setBulkAudience] = useState("contacts");
  const [bulkSegments, setBulkSegments] = useState([]);
  const [bulkSelectedSegments, setBulkSelectedSegments] = useState([]);
  // Arriving from Engage → Funnel ("Create segment"): start with that segment as the audience.
  const routeLocation = useLocation();
  useEffect(() => {
    const ids = routeLocation.state?.presetSegmentIds;
    if (Array.isArray(ids) && ids.length) {
      setBulkAudience("segment");
      setBulkSelectedSegments(ids);
      window.history.replaceState({}, document.title);
    }
  }, [routeLocation.state]);
  const [bulkAudienceCount, setBulkAudienceCount] = useState(null);
  const [bulkAudienceCounting, setBulkAudienceCounting] = useState(false);

  /** Bulk: multi-select from saved contacts + optional pasted lines */
  const [bulkRecipientSelect, setBulkRecipientSelect] = useState([]);
  const [bulkTemplateName, setBulkTemplateName] = useState("hello_world");
  const [bulkLanguageCode, setBulkLanguageCode] = useState("en_US");
  /** '' = use fields as-is; 'custom' = manual entry; else key name\x1flanguage from Meta list */
  const [bulkTemplatePicker, setBulkTemplatePicker] = useState("");
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkSummary, setBulkSummary] = useState(null);
  /** Comma-separated values for {{1}}, {{2}}, … (same for every recipient on bulk). */
  const [bulkBodyParams, setBulkBodyParams] = useState("");
  /** Value for a dynamic URL button ({{1}} in the button link), when the template has one. */
  const [bulkButtonParam, setBulkButtonParam] = useState("");
  /** Offer/coupon code when the template has a COPY_CODE button. */
  const [bulkCopyCodeParam, setBulkCopyCodeParam] = useState("");
  const [bulkHeaderImageUrl, setBulkHeaderImageUrl] = useState("");
  const [bulkHeaderImageFile, setBulkHeaderImageFile] = useState(null);
  const [bulkHeaderImagePreview, setBulkHeaderImagePreview] = useState("");
  const [bulkHeaderLat, setBulkHeaderLat] = useState("");
  const [bulkHeaderLng, setBulkHeaderLng] = useState("");

  /** WhatsApp → Contacts (same API as Contacts page) for test / bulk pickers */
  const [contactsForPicker, setContactsForPicker] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  /** From API when server has Meta Embedded Signup env; same for all tenants (one Meta app). */
  const [embeddedSignup, setEmbeddedSignup] = useState({ available: false });
  /** Backend has WHATSAPP_META_APP_ID + WHATSAPP_META_APP_SECRET → auto long-lived token exchange on save */
  const [metaTokenExchangeEnabled, setMetaTokenExchangeEnabled] = useState(false);

  const load = useCallback(() => {
    if (!appId) return;
    setLoading(true);
    fetchWithAuth(Utils.baseurl + "application/whatsapp/get-account", {
      method: "POST",
      body: JSON.stringify({ app_id: appId }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setLoading(false);
        if (result.status) {
          setEmbeddedSignup(
            result.embeddedSignup && typeof result.embeddedSignup === "object"
              ? result.embeddedSignup
              : { available: false }
          );
          setMetaTokenExchangeEnabled(Boolean(result.metaTokenExchangeEnabled));
        }
        if (result.status && result.data) {
          const d = result.data;
          setAccount(d);
          setPhoneNumberId(d.phoneNumberId || "");
          setWabaId(d.wabaId || "");
          setBusinessName(d.businessName || "");
          setWebhookVerifyToken(d.webhookVerifyToken || "");
        } else {
          setAccount(null);
          setPhoneNumberId("");
          setWabaId("");
          setBusinessName("");
          setWebhookVerifyToken("");
        }
      })
      .catch(() => {
        setLoading(false);
        toast.error("Network error");
      });
  }, [appId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!createTplHeaderFile) {
      setCreateTplHeaderPreview("");
      return undefined;
    }
    const url = URL.createObjectURL(createTplHeaderFile);
    setCreateTplHeaderPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [createTplHeaderFile]);

  useEffect(() => {
    if (!bulkHeaderImageFile) {
      setBulkHeaderImagePreview("");
      return undefined;
    }
    const url = URL.createObjectURL(bulkHeaderImageFile);
    setBulkHeaderImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [bulkHeaderImageFile]);

  const uploadWhatsappHeaderMedia = async (file, opts = {}) => {
    const fd = new FormData();
    fd.append("app_id", appId);
    fd.append("file", file);
    if (opts.template_category) fd.append("template_category", opts.template_category);
    if (opts.usage_type != null) fd.append("usage_type", String(opts.usage_type));
    const res = await fetchWithAuth(Utils.baseurl + "application/whatsapp/media/upload", {
      method: "POST",
      body: fd,
    });
    const result = await res.json().catch(() => ({}));
    if (!result.status || !result.data?.id) {
      throw new Error(result.message || "Header image upload failed");
    }
    return String(result.data.id);
  };

  // force=true (the "Refresh" button) resyncs the local mirror from Meta before reading it -
  // normal loads (page open, after create/delete) just read the mirror, which is already kept
  // fresh by that resync-on-stale check server-side and by the WhatsApp webhook in real time.
  const loadTemplates = useCallback((force = false) => {
    if (!appId) return;
    setTemplateListLoading(true);
    fetchWithAuth(Utils.baseurl + "application/whatsapp/templates/list", {
      method: "POST",
      body: JSON.stringify({ app_id: appId, limit: 50, forceSync: force ? 1 : undefined }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setTemplateListLoading(false);
        if (result.status && result.data) {
          const graph = result.data;
          const rows = Array.isArray(graph.data) ? graph.data : [];
          setTemplateRows(rows);
          setSelectedTemplateIds((prev) => {
            const valid = new Set(
              rows.map((r) => (r && r.id != null ? String(r.id) : "")).filter(Boolean)
            );
            return prev.filter((id) => valid.has(id));
          });
        } else {
          setTemplateRows([]);
          toast.error(result.message || "Could not load templates");
        }
      })
      .catch(() => {
        setTemplateListLoading(false);
        toast.error("Network error");
      });
  }, [appId]);

  useEffect(() => {
    if ((section === "templates" || section === "bulk") && appId) {
      loadTemplates();
    }
  }, [section, appId, loadTemplates]);

  const loadContactsForPicker = useCallback(() => {
    if (!appId) return;
    setContactsLoading(true);
    fetchWithAuth(Utils.baseurl + "application/whatsapp/contacts/list", {
      method: "POST",
      body: JSON.stringify({ app_id: appId, limit: 200, offset: 0 }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setContactsLoading(false);
        if (result.status && Array.isArray(result.data)) {
          setContactsForPicker(result.data);
        } else {
          setContactsForPicker([]);
        }
      })
      .catch(() => {
        setContactsLoading(false);
        toast.error("Could not load contacts");
      });
  }, [appId]);

  useEffect(() => {
    if (section === "bulk" && appId) {
      loadContactsForPicker();
    }
  }, [section, appId, loadContactsForPicker]);

  // Load active segments only (paused must not appear in Send Message audience).
  useEffect(() => {
    if (section !== "bulk" || !appId) return;
    fetchWithAuth(Utils.baseurl + "get/segments", {
      method: "POST",
      body: JSON.stringify({ app_id: appId, status: "active" }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        if (result.status && Array.isArray(result.data)) setBulkSegments(result.data);
      })
      .catch(() => {});
  }, [section, appId]);

  // Live reachable-count for the chosen audience (subscribed / segment / test).
  useEffect(() => {
    if (section !== "bulk" || !appId || bulkAudience === "contacts") {
      setBulkAudienceCount(null);
      return;
    }
    if (bulkAudience === "segment" && bulkSelectedSegments.length === 0) {
      setBulkAudienceCount(null);
      return;
    }
    let cancelled = false;
    setBulkAudienceCounting(true);
    fetchWithAuth(Utils.baseurl + "application/whatsapp/audience/count", {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        audience_type: bulkAudience,
        ...(bulkAudience === "segment" ? { segment_ids: bulkSelectedSegments } : {}),
      }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        if (cancelled) return;
        setBulkAudienceCounting(false);
        setBulkAudienceCount(result.status ? result.data?.reachable ?? 0 : 0);
      })
      .catch(() => {
        if (!cancelled) {
          setBulkAudienceCounting(false);
          setBulkAudienceCount(0);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [section, appId, bulkAudience, bulkSelectedSegments]);

  const contactSelectOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const r of contactsForPicker || []) {
      const digits = String(r.phoneE164 ?? "").replace(/\D/g, "");
      if (!digits || seen.has(digits)) continue;
      seen.add(digits);
      const name = (r.name && String(r.name).trim()) || "Contact";
      const src =
        r.source === "app_user"
          ? "App users"
          : r.source === "whatsapp"
            ? "WhatsApp"
            : "Contact";
      out.push({ value: digits, label: `${name} · ${digits} (${src})` });
    }
    out.sort((a, b) => a.label.localeCompare(b.label));
    return out;
  }, [contactsForPicker]);

  /** Manual phone entry in recipient pickers: digits only, country code required (≥11 digits). */
  const manualPhoneDigits = (input) => String(input || "").replace(/\D/g, "");
  const isValidManualPhone = (inputValue) => {
    const d = manualPhoneDigits(inputValue);
    return d.length >= 11 && d.length <= 15;
  };
  const manualPhoneOption = (inputValue) => {
    const d = manualPhoneDigits(inputValue);
    return { value: d, label: `${d} (manual)` };
  };
  const formatCreateLabel = (inputValue) => {
    const d = manualPhoneDigits(inputValue);
    return `Send to number: ${d}`;
  };

  /** Meta template review status — same pill badges as the rest of the app. */
  const templateStatusBadge = (row) => {
    const s = String(row.status || "").toUpperCase();
    const m = getWhatsAppTemplateStatusDisplay(row.status);
    const reason =
      s === "REJECTED" && row.rejected_reason && row.rejected_reason !== "NONE"
        ? String(row.rejected_reason).replace(/_/g, " ").toLowerCase()
        : "";
    return (
      <span>
        <span className={m.badgeClass}>{m.label}</span>
        {reason ? (
          <div className="small text-danger mt-1" style={{ maxWidth: 220 }}>
            {reason}
          </div>
        ) : null}
      </span>
    );
  };

  /** Body text preview from Meta components (what the customer will read). */
  const templateBodyPreview = (row) => {
    const comps = Array.isArray(row.components) ? row.components : [];
    const body = comps.find((c) => String(c.type || "").toUpperCase() === "BODY");
    const text = body && body.text ? String(body.text) : "";
    if (!text) return "—";
    return text.length > 120 ? `${text.slice(0, 120)}…` : text;
  };

  /** Live preview: substitute {{n}} with example values while typing. */
  const tplPreviewBody = () => {
    let text = createTplBody || "";
    const examples = String(createTplExample || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    examples.forEach((v, i) => {
      text = text.split(`{{${i + 1}}}`).join(v);
    });
    return text;
  };

  const applyTplBodySelection = (mutate) => {
    const el = createTplBodyRef.current;
    const start = el ? el.selectionStart : createTplBody.length;
    const end = el ? el.selectionEnd : createTplBody.length;
    const { next, selStart, selEnd } = mutate(createTplBody, start, end);
    setCreateTplBody(next);
    requestAnimationFrame(() => {
      const field = createTplBodyRef.current;
      if (!field) return;
      field.focus();
      field.setSelectionRange(selStart, selEnd);
    });
  };

  const applyTplBodyWrap = (open, close) => {
    applyTplBodySelection((value, start, end) => wrapTextSelection(value, start, end, open, close));
  };

  const insertTplBodyVariable = () => {
    const n = tplVarCount() + 1;
    applyTplBodySelection((value, start, end) => insertTextAtSelection(value, start, end, `{{${n}}}`));
  };

  const tplVarCount = () => {
    const re = /\{\{(\d+)\}\}/g;
    let max = 0;
    let m;
    while ((m = re.exec(String(createTplBody || ""))) !== null) {
      const n = parseInt(m[1], 10);
      if (n > max) max = n;
    }
    return max;
  };

  const connectionTokenStatus = useMemo(() => {
    if (!account?.tokenExpiresAt) return null;
    const d = new Date(account.tokenExpiresAt);
    if (Number.isNaN(d.getTime())) return null;
    const ms = d.getTime() - Date.now();
    if (ms < 0) {
      return {
        className: "alert-danger",
        text: `Stored Meta token expired (${d.toLocaleString()}). Generate a new token in Meta and save again.`,
      };
    }
    if (ms < 7 * 24 * 60 * 60 * 1000) {
      return {
        className: "alert-warning",
        text: `Token expires soon (${d.toLocaleString()}). After ~60 days, paste a new token or use a System User token in Meta Business Manager.`,
      };
    }
    return {
      className: "alert-secondary",
      text: `Token valid until ${d.toLocaleString()} (Meta).`,
    };
  }, [account?.tokenExpiresAt]);

  const contactsSelectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: 38,
        borderColor: state.isFocused ? "rgb(67, 70, 206)" : "#ced4da",
        boxShadow: state.isFocused ? "0 0 0 1px rgb(67, 70, 206)" : "none",
        "&:hover": {
          borderColor: state.isFocused ? "rgb(67, 70, 206)" : "#adb5bd",
        },
      }),
      menu: (base) => ({ ...base, zIndex: 10001 }),
      menuPortal: (base) => ({ ...base, zIndex: 10001 }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? "rgb(67, 70, 206)"
          : state.isFocused
            ? "rgba(67, 70, 206, 0.08)"
            : "white",
        color: state.isSelected ? "#fff" : "#212529",
      }),
      valueContainer: (base) => ({ ...base, paddingTop: 2, paddingBottom: 2 }),
      multiValue: (base) => ({ ...base, backgroundColor: "rgba(67, 70, 206, 0.12)" }),
    }),
    []
  );

  const templateRowsWithId = useMemo(
    () => templateRows.filter((r) => r && r.id),
    [templateRows]
  );

  const filteredTemplateRows = useMemo(() => {
    const wantName = String(appliedTemplateFilterName || "").trim().toLowerCase();
    const wantStatus = String(appliedTemplateFilterStatus || "").toUpperCase();
    const wantCategory = String(appliedTemplateFilterCategory || "").toUpperCase();
    return templateRows.filter((r) => {
      if (wantName && !String(r.name || "").toLowerCase().includes(wantName)) return false;
      if (wantStatus && String(r.status || "").toUpperCase() !== wantStatus) return false;
      if (wantCategory && String(r.category || "").toUpperCase() !== wantCategory) return false;
      return true;
    });
  }, [templateRows, appliedTemplateFilterName, appliedTemplateFilterStatus, appliedTemplateFilterCategory]);

  const templateStatusSelectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: TPL_BTN_FIELD_HEIGHT,
        height: TPL_BTN_FIELD_HEIGHT,
        boxSizing: "border-box",
        alignItems: "center",
        borderColor: state.isFocused ? "rgb(67, 70, 206)" : "#ced4da",
        boxShadow: state.isFocused ? "0 0 0 1px rgb(67, 70, 206)" : "none",
        "&:hover": {
          borderColor: state.isFocused ? "rgb(67, 70, 206)" : "#adb5bd",
        },
      }),
      valueContainer: (base) => ({
        ...base,
        height: TPL_BTN_FIELD_HEIGHT,
        paddingTop: 0,
        paddingBottom: 0,
      }),
      indicatorsContainer: (base) => ({
        ...base,
        height: TPL_BTN_FIELD_HEIGHT,
      }),
      dropdownIndicator: (base) => ({
        ...base,
        padding: "4px 8px",
      }),
      input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
      }),
      menu: (base) => ({ ...base, zIndex: 10001 }),
      menuPortal: (base) => ({ ...base, zIndex: 10001 }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? "rgb(67, 70, 206)"
          : state.isFocused
            ? "rgba(67, 70, 206, 0.08)"
            : "white",
        color: state.isSelected ? "#fff" : "#212529",
      }),
    }),
    []
  );

  const draftTemplateStatusSelectValue = useMemo(() => {
    const v = draftTemplateFilterStatus ?? "";
    return WA_TEMPLATE_STATUS_FILTER_OPTIONS.find((o) => o.value === v) || WA_TEMPLATE_STATUS_FILTER_OPTIONS[0];
  }, [draftTemplateFilterStatus]);

  const draftTemplateCategorySelectValue = useMemo(() => {
    const v = draftTemplateFilterCategory ?? "";
    return (
      WA_TEMPLATE_CATEGORY_FILTER_OPTIONS.find((o) => o.value === v) || WA_TEMPLATE_CATEGORY_FILTER_OPTIONS[0]
    );
  }, [draftTemplateFilterCategory]);

  const handleApplyTemplateFilter = useCallback(() => {
    const nextName = (draftTemplateFilterName ?? "").trim();
    const nextStatus = draftTemplateFilterStatus ?? "";
    const nextCategory = draftTemplateFilterCategory ?? "";
    setAppliedTemplateFilterName(nextName);
    setAppliedTemplateFilterStatus(nextStatus);
    setAppliedTemplateFilterCategory(nextCategory);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (!nextName) p.delete("name");
      else p.set("name", nextName);
      if (!nextStatus) p.delete("status");
      else p.set("status", nextStatus);
      if (!nextCategory) p.delete("category");
      else p.set("category", nextCategory);
      return p;
    });
  }, [draftTemplateFilterName, draftTemplateFilterStatus, draftTemplateFilterCategory, setSearchParams]);

  const handleResetTemplateFilter = useCallback(() => {
    setDraftTemplateFilterName("");
    setAppliedTemplateFilterName("");
    setDraftTemplateFilterStatus("");
    setAppliedTemplateFilterStatus("");
    setDraftTemplateFilterCategory("");
    setAppliedTemplateFilterCategory("");
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete("name");
      p.delete("status");
      p.delete("category");
      return p;
    });
  }, [setSearchParams]);

  /** Sync ?status= / ?category= from URL ↔ state; browser back removes filter from URL → reset table filter. */
  useEffect(() => {
    if (section !== "templates") {
      prevTemplateNameQueryRef.current = "";
      prevTemplateStatusQueryRef.current = "";
      prevTemplateCategoryQueryRef.current = "";
      return;
    }
    const qName = (searchParams.get("name") || "").trim();
    if (!qName) {
      if (prevTemplateNameQueryRef.current) {
        setDraftTemplateFilterName("");
        setAppliedTemplateFilterName("");
      }
      prevTemplateNameQueryRef.current = "";
    } else {
      prevTemplateNameQueryRef.current = qName;
      setDraftTemplateFilterName(qName);
      setAppliedTemplateFilterName(qName);
    }

    const qStatus = (searchParams.get("status") || "").trim();
    if (!qStatus) {
      if (prevTemplateStatusQueryRef.current) {
        setDraftTemplateFilterStatus("");
        setAppliedTemplateFilterStatus("");
      }
      prevTemplateStatusQueryRef.current = "";
    } else {
      prevTemplateStatusQueryRef.current = qStatus;
      const upper = qStatus.toUpperCase();
      if (upper === "ALL") {
        setDraftTemplateFilterStatus("");
        setAppliedTemplateFilterStatus("");
      } else {
        const match = WA_TEMPLATE_STATUS_FILTER_OPTIONS.find((o) => o.value === upper);
        if (match) {
          setDraftTemplateFilterStatus(match.value);
          setAppliedTemplateFilterStatus(match.value);
        }
      }
    }

    const qCategory = (searchParams.get("category") || "").trim();
    if (!qCategory) {
      if (prevTemplateCategoryQueryRef.current) {
        setDraftTemplateFilterCategory("");
        setAppliedTemplateFilterCategory("");
      }
      prevTemplateCategoryQueryRef.current = "";
    } else {
      prevTemplateCategoryQueryRef.current = qCategory;
      const upper = qCategory.toUpperCase();
      if (upper === "ALL") {
        setDraftTemplateFilterCategory("");
        setAppliedTemplateFilterCategory("");
      } else {
        const match = WA_TEMPLATE_CATEGORY_FILTER_OPTIONS.find((o) => o.value === upper);
        if (match) {
          setDraftTemplateFilterCategory(match.value);
          setAppliedTemplateFilterCategory(match.value);
        }
      }
    }
  }, [section, searchParams]);

  const templateFilterHasActiveContent = useMemo(() => {
    const qName = (searchParams.get("name") || "").trim();
    const qStatus = (searchParams.get("status") || "").trim();
    const qCategory = (searchParams.get("category") || "").trim();
    const urlActive =
      Boolean(qName) ||
      Boolean(qStatus && qStatus.toLowerCase() !== "all") ||
      Boolean(qCategory && qCategory.toLowerCase() !== "all");
    const draftActive =
      (draftTemplateFilterName ?? "") !== "" ||
      (draftTemplateFilterStatus ?? "") !== "" ||
      (draftTemplateFilterCategory ?? "") !== "";
    const appliedActive =
      (appliedTemplateFilterName ?? "") !== "" ||
      (appliedTemplateFilterStatus ?? "") !== "" ||
      (appliedTemplateFilterCategory ?? "") !== "";
    return urlActive || draftActive || appliedActive;
  }, [
    searchParams,
    draftTemplateFilterName,
    draftTemplateFilterStatus,
    draftTemplateFilterCategory,
    appliedTemplateFilterName,
    appliedTemplateFilterStatus,
    appliedTemplateFilterCategory,
  ]);

  useEffect(() => {
    if (section !== "templates") return;
    setTemplateFilterAccordionOpen(templateFilterHasActiveContent);
  }, [section, templateFilterHasActiveContent]);

  const filteredTemplateRowsWithId = useMemo(
    () => filteredTemplateRows.filter((r) => r && r.id),
    [filteredTemplateRows]
  );

  useEffect(() => {
    const el = templateSelectAllRef.current;
    if (!el || el.type !== "checkbox") return;
    const n = filteredTemplateRowsWithId.length;
    const sel = filteredTemplateRowsWithId.filter((r) =>
      selectedTemplateIds.includes(String(r.id))
    ).length;
    el.indeterminate = n > 0 && sel > 0 && sel < n;
  }, [filteredTemplateRowsWithId, selectedTemplateIds]);

  const toggleTemplateSelected = (row) => {
    const id = String(row.id);
    setSelectedTemplateIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllTemplates = () => {
    const allIds = filteredTemplateRowsWithId.map((r) => String(r.id));
    setSelectedTemplateIds((prev) => {
      if (allIds.length === 0) return [];
      const allSelected = allIds.every((i) => prev.includes(i));
      return allSelected ? [] : [...allIds];
    });
  };

  const closeTemplateActionsDropdown = useCallback(() => {
    setOpenTemplateActionId(null);
    setTemplateDropdownPosition(null);
    setTemplateDropdownAnchorRect(null);
  }, []);

  const getTemplateDropdownPos = useCallback((anchorRect, menuW, menuH, boundaryRect) => {
    return getAnchoredBoundPosition(anchorRect, menuW, menuH, boundaryRect);
  }, []);

  useDropdownReposition({
    isOpen: Boolean(openTemplateActionId && templateDropdownAnchorRect),
    anchorRect: templateDropdownAnchorRect,
    dropdownRef: templateDropdownRef,
    boundaryRect: templateTableScrollRef.current?.getBoundingClientRect?.(),
    getPosition: getTemplateDropdownPos,
    onPosition: setTemplateDropdownPosition,
  });

  useEffect(() => {
    if (!openTemplateActionId) return undefined;
    const handleClickOutside = (event) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(event.target)) {
        closeTemplateActionsDropdown();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openTemplateActionId, closeTemplateActionsDropdown]);

  useEffect(() => {
    if (!openTemplateActionId) return undefined;
    const closeOnScroll = () => closeTemplateActionsDropdown();
    const tableEl = templateTableScrollRef.current;
    window.addEventListener("scroll", closeOnScroll, true);
    if (tableEl) tableEl.addEventListener("scroll", closeOnScroll);
    return () => {
      window.removeEventListener("scroll", closeOnScroll, true);
      if (tableEl) tableEl.removeEventListener("scroll", closeOnScroll);
    };
  }, [openTemplateActionId, closeTemplateActionsDropdown]);

  const handleOpenTemplateActions = useCallback(
    (event, row) => {
      const idStr = String(row?.id || `${row?.name || ""}-${row?.language || ""}`);
      if (!idStr) return;
      if (openTemplateActionId === idStr) {
        closeTemplateActionsDropdown();
        return;
      }
      const btn = event?.currentTarget;
      if (btn) {
        const rect = btn.getBoundingClientRect();
        setTemplateDropdownAnchorRect(rect);
        const boundaryRect = templateTableScrollRef.current?.getBoundingClientRect?.();
        setTemplateDropdownPosition(getAnchoredBoundPosition(rect, 220, 64, boundaryRect));
      }
      setOpenTemplateActionId(idStr);
    },
    [openTemplateActionId, closeTemplateActionsDropdown]
  );

  const handleDeleteTemplate = async (row) => {
    if (!appId || !row || !row.id) {
      toast.error("This row has no template id — refresh the list and try again.");
      return;
    }
    const label = `${row.name} (${row.language})`;
    const { isConfirmed } = await Swal.fire({
      title: "Delete template?",
      text: `Delete template "${label}" from Meta? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d6efd",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });
    if (!isConfirmed) {
      return;
    }
    const idStr = String(row.id);
    setTemplateDeletingId(idStr);
    fetchWithAuth(Utils.baseurl + "application/whatsapp/templates/delete", {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        hsm_id: idStr,
        name: String(row.name || "").trim(),
        ...(row.language ? { language: String(row.language).trim() } : {}),
      }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setTemplateDeletingId(null);
        if (result.status) {
          toast.success(result.message || "Template deleted");
          setSelectedTemplateIds((prev) => prev.filter((x) => x !== idStr));
          loadTemplates();
        } else {
          toast.error(result.message || "Delete failed");
          if (result.hint) toast.info(result.hint, { autoClose: 8000 });
        }
      })
      .catch(() => {
        setTemplateDeletingId(null);
        toast.error("Network error");
      });
  };

  const handleDeleteSelectedTemplates = async () => {
    if (!appId) return;
    const rows = templateRowsWithId.filter((r) => selectedTemplateIds.includes(String(r.id)));
    if (!rows.length) {
      toast.error("Select at least one template");
      return;
    }
    const { isConfirmed } = await Swal.fire({
      title: "Delete selected templates?",
      text: `Delete ${rows.length} template(s) from Meta? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d6efd",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });
    if (!isConfirmed) {
      return;
    }
    setTemplateBulkDeleting(true);
    fetchWithAuth(Utils.baseurl + "application/whatsapp/templates/delete", {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        items: rows.map((r) => ({
          hsm_id: String(r.id),
          name: String(r.name || "").trim(),
          ...(r.language ? { language: String(r.language).trim() } : {}),
        })),
      }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setTemplateBulkDeleting(false);
        if (result.status) {
          const failed = result.data && Number(result.data.failed) > 0;
          if (failed) {
            toast.warning(result.message || "Some deletes failed");
            if (Array.isArray(result.data.results)) {
              const firstErr = result.data.results.find((x) => !x.ok && x.error);
              if (firstErr && firstErr.error) toast.error(String(firstErr.error), { autoClose: 7000 });
            }
          } else {
            toast.success(result.message || "Done");
          }
          setSelectedTemplateIds([]);
          loadTemplates();
        } else {
          toast.error(result.message || "Delete failed");
          if (result.hint) toast.info(result.hint, { autoClose: 8000 });
        }
      })
      .catch(() => {
        setTemplateBulkDeleting(false);
        toast.error("Network error");
      });
  };

  const approvedBulkTemplates = useMemo(
    () =>
      templateRows.filter(
        (t) => String(t.status || "").toUpperCase() === "APPROVED"
      ),
    [templateRows]
  );

  const TPL_KEY_SEP = "\x1f";
  const bulkTplKey = (row) => `${row.name}${TPL_KEY_SEP}${row.language || ""}`;

  /** Selected approved template row (for live preview on Send Message). */
  const bulkSelectedRow =
    bulkTemplatePicker && bulkTemplatePicker !== "custom"
      ? approvedBulkTemplates.find((r) => bulkTplKey(r) === bulkTemplatePicker) || null
      : null;

  /** Saved header media of the selected template: is it complete (sendable without a new upload)? */
  const bulkHeaderCheck = useWhatsAppHeaderCheck({
    appId,
    template: bulkSelectedRow,
    skip: Boolean(bulkHeaderImageFile || String(bulkHeaderImageUrl || "").trim()),
  });

  const bulkTemplateBodyText = () => {
    const comps = bulkSelectedRow && Array.isArray(bulkSelectedRow.components) ? bulkSelectedRow.components : [];
    const body = comps.find((c) => String(c.type || "").toUpperCase() === "BODY");
    return body && body.text ? String(body.text) : "";
  };

  const bulkPreviewBody = () => {
    let text = bulkTemplateBodyText();
    if (!text) return "";
    const vals = String(bulkBodyParams || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    vals.forEach((v, i) => {
      text = text.split(`{{${i + 1}}}`).join(v);
    });
    return text;
  };

  const bulkVarCount = () => {
    const re = /\{\{(\d+)\}\}/g;
    let max = 0;
    let m;
    const s = bulkTemplateBodyText();
    while ((m = re.exec(s)) !== null) {
      const n = parseInt(m[1], 10);
      if (n > max) max = n;
    }
    return max;
  };

  const bulkVarValuesCount = () =>
    String(bulkBodyParams || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean).length;

  const PLATFORM_SECTIONS = [
    "inbox",
    "contacts",
    "campaigns",
    "analytics",
    "phones",
  ];
  if (section && PLATFORM_SECTIONS.includes(section)) {
    return <WhatsAppPlatform section={section} appId={appId} />;
  }

  const mode =
    section === "templates"
      ? "templates"
      : section === "bulk"
      ? "bulk"
      : section === "connection" || !section
      ? "connection"
      : null;
  if (section && mode === null) {
    return <Navigate to={`/apps/${appId}/whatsapp/connection`} replace />;
  }
  const showConnection = mode === "connection";
  const showTemplates = mode === "templates";
  const showBulk = mode === "bulk";

  const handleSave = (e) => {
    e.preventDefault();
    if (!appId) return;
    setSaving(true);
    const body = {
      app_id: appId,
      phone_number_id: phoneNumberId.trim(),
      waba_id: wabaId.trim() || undefined,
      business_name: businessName.trim() || undefined,
      webhook_verify_token: webhookVerifyToken.trim() || undefined,
    };
    if (accessToken.trim()) {
      body.access_token = accessToken.trim();
    }
    fetchWithAuth(Utils.baseurl + "application/whatsapp/save-account", {
      method: "POST",
      body: JSON.stringify(body),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setSaving(false);
        if (result.status) {
          toast.success(result.message || "Saved", { autoClose: result.data?.tokenUpgraded ? 10000 : 5000 });
          setAccessToken("");
          load();
        } else {
          toast.error(result.message || "Save failed");
        }
      })
      .catch(() => {
        setSaving(false);
        toast.error("Network error");
      });
  };


  const loadLibraryTemplates = () => {
    if (!appId) return;
    setLibLoading(true);
    setLibPick(null);
    fetchWithAuth(Utils.baseurl + "application/whatsapp/templates/library/list", {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        search: libSearch.trim() || undefined,
        category: libCategory || undefined,
        limit: 25,
      }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setLibLoading(false);
        if (result.status) {
          setLibRows(Array.isArray(result.data?.data) ? result.data.data : []);
          setLibBrowseSupported(result.librarySupported !== false);
        } else {
          setLibRows([]);
          toast.error(result.message || "Could not load Meta template library");
          if (result.hint) toast.info(result.hint, { autoClose: 8000 });
        }
      })
      .catch(() => {
        setLibLoading(false);
        toast.error("Network error");
      });
  };

  const handleCreateFromLibrary = (pickOverride) => {
    const pick = pickOverride || libPick;
    if (!appId || !pick || !pick.name) return;
    const hasUrlButton =
      Array.isArray(pick.buttons) && pick.buttons.some((b) => String(b.type || "").toUpperCase() === "URL");
    if (hasUrlButton && !libButtonBaseUrl.trim()) {
      toast.error("This library template has a link button — enter your website URL for it.");
      return;
    }
    setLibCreating(true);
    fetchWithAuth(Utils.baseurl + "application/whatsapp/templates/create-from-library", {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        name: libNewName.trim() || pick.name,
        library_template_name: pick.name,
        category: pick.category || libCategory,
        language: pick.language || "en_US",
        ...(libButtonBaseUrl.trim() ? { button_base_url: libButtonBaseUrl.trim() } : {}),
      }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setLibCreating(false);
        if (result.status) {
          toast.success(result.message || "Template created from library");
          setShowCreateTemplateModal(false);
          setLibPick(null);
          setLibNewName("");
          setLibButtonBaseUrl("");
          loadTemplates();
        } else {
          toast.error(result.message || "Create failed");
          if (result.hint) toast.info(result.hint, { autoClose: 8000 });
        }
      })
      .catch(() => {
        setLibCreating(false);
        toast.error("Network error");
      });
  };

  const handleCreateTemplate = (e) => {
    e.preventDefault();
    if (!appId) return;
    if (createTplCategory === "AUTHENTICATION") {
      if (!createTplName.trim()) {
        toast.error("Enter a template name (e.g. otp_login)");
        return;
      }
    } else if (!createTplBody.trim()) {
      toast.error("Body text is required");
      return;
    }
    for (const b of createTplButtons || []) {
      const opt = findOptionByValue(WA_TEMPLATE_BUTTON_TYPES, Number(b.type));
      const apiType = opt?.apiType || "";
      if (apiType === "URL") {
        if (!String(b.text || "").trim() || !String(b.url || "").trim()) {
          toast.error("URL buttons need a label and a website URL");
          return;
        }
        const dynamic =
          String(b.urlType || "").toLowerCase() === "dynamic" || /\{\{\d+\}\}/.test(String(b.url || ""));
        if (dynamic && !String(b.example || "").trim()) {
          toast.error("Dynamic website buttons need an example URL path for Meta review");
          return;
        }
      } else if (apiType === "PHONE_NUMBER") {
        if (!String(b.text || "").trim() || !String(b.phone_number || "").trim()) {
          toast.error("Phone buttons need a label and phone number");
          return;
        }
      } else if (apiType === "QUICK_REPLY") {
        if (!String(b.text || "").trim()) {
          toast.error("Custom buttons need a label");
          return;
        }
      } else if (apiType === "COPY_CODE") {
        if (!String(b.example || "").trim()) {
          toast.error("Copy offer code buttons need a sample coupon code");
          return;
        }
      }
    }
    if (
      createTplCategory !== "AUTHENTICATION" &&
      isFileHeaderFormat(createTplHeaderType) &&
      !createTplHeaderFile
    ) {
      toast.error("Upload a header sample for Meta review (same as WhatsApp Manager → Media sample)");
      return;
    }
    let body_example_row;
    if (createTplExample.trim()) {
      body_example_row = createTplExample
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    const basePayload = {
      app_id: appId,
      name: createTplName.trim() || undefined,
      language: createTplLang.trim() || "en_US",
      category: createTplCategory,
    };
    if (createTplCategory === "AUTHENTICATION") {
      if (authOtpType === "ZERO_TAP" && !authZeroTapTermsAccepted) {
        toast.error("Accept the WhatsApp zero-tap terms to continue");
        return;
      }
      if (authOtpType !== "COPY_CODE") {
        const apps = (authSupportedApps || [])
          .map((a) => ({
            package_name: String(a.package_name || "").trim(),
            signature_hash: String(a.signature_hash || "").trim(),
          }))
          .filter((a) => a.package_name || a.signature_hash);
        if (!apps.length || apps.some((a) => !a.package_name || a.signature_hash.length !== 11)) {
          toast.error(
            "Autofill/zero-tap needs at least one Android package name and an 11-character app signature hash per app"
          );
          return;
        }
      }
      const supportedApps = (authSupportedApps || [])
        .map((a) => ({
          package_name: String(a.package_name || "").trim(),
          signature_hash: String(a.signature_hash || "").trim(),
        }))
        .filter((a) => a.package_name && a.signature_hash.length === 11);
      Object.assign(basePayload, {
        auth_add_security_recommendation: authAddSecurityRecommendation,
        auth_code_expiration_minutes: Number(authCodeExpirationMinutes) || undefined,
        auth_otp_type: authOtpType,
        ...(authOtpType === "ZERO_TAP"
          ? { auth_zero_tap_terms_accepted: true }
          : {}),
        ...(authCopyButtonText.trim()
          ? { auth_copy_button_text: authCopyButtonText.trim() }
          : {}),
        ...(Number(authMessageTtlSeconds) > 0
          ? { message_send_ttl_seconds: Number(authMessageTtlSeconds) }
          : {}),
        ...(authOtpType !== "COPY_CODE"
          ? {
              auth_supported_apps: supportedApps,
              auth_package_name: supportedApps[0]?.package_name,
              auth_signature_hash: supportedApps[0]?.signature_hash,
              ...(authAutofillText.trim()
                ? { auth_autofill_text: authAutofillText.trim() }
                : {}),
            }
          : {}),
      });
    } else {
      const buttonsPayload = (createTplButtons || [])
        .map((b) => {
          const opt = findOptionByValue(WA_TEMPLATE_BUTTON_TYPES, Number(b.type));
          const apiType = opt?.apiType;
          if (!apiType) return null;
          if (apiType === "URL") {
            const base = String(b.url || "").trim().replace(/\{\{\d+\}\}\s*$/, "");
            const dynamic =
              String(b.urlType || "").toLowerCase() === "dynamic" ||
              /\{\{\d+\}\}/.test(String(b.url || ""));
            const url = dynamic ? `${base}{{1}}` : base;
            const exRaw = String(b.example || "").trim();
            const example =
              dynamic && exRaw
                ? /^https?:\/\//i.test(exRaw)
                  ? exRaw
                  : `${base}${exRaw.startsWith("/") ? exRaw : `/${exRaw}`}`
                : "";
            return {
              type: apiType,
              text: String(b.text || "").trim(),
              url,
              example,
            };
          }
          return {
            type: apiType,
            text: String(b.text || "").trim(),
            phone_number: String(b.phone_number || "").trim(),
            example: String(b.example || "").trim(),
          };
        })
        .filter(Boolean);
      Object.assign(basePayload, {
        body_text: createTplBody.trim(),
        ...(isFileHeaderFormat(createTplHeaderType) || createTplHeaderType === "LOCATION"
          ? { header_format: createTplHeaderType }
          : { header_text: createTplHeader.trim() || undefined }),
        footer_text: createTplFooter.trim() || undefined,
        body_example_row: body_example_row && body_example_row.length ? body_example_row : undefined,
        ...(buttonsPayload.length ? { buttons: buttonsPayload } : {}),
      });
    }
    setTplCreating(true);
    const sendCreate = (body) =>
      fetchWithAuth(Utils.baseurl + "application/whatsapp/templates/create", {
        method: "POST",
        body,
      });
    let createReq;
    if (createTplCategory !== "AUTHENTICATION" && isFileHeaderFormat(createTplHeaderType)) {
      const fd = new FormData();
      Object.entries(basePayload).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (k === "body_example_row" || k === "buttons") fd.append(k, JSON.stringify(v));
        else fd.append(k, String(v));
      });
      fd.append("header_image", createTplHeaderFile);
      createReq = sendCreate(fd);
    } else {
      createReq = sendCreate(JSON.stringify(basePayload));
    }
    createReq
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setTplCreating(false);
        if (result.status) {
          toast.success(result.message || "Template submitted");
          setShowCreateTemplateModal(false);
          setAuthZeroTapTermsAccepted(false);
          setCreateTplHeaderType("NONE");
          setCreateTplHeaderFile(null);
          setCreateTplButtons([]);
          loadTemplates();
        } else {
          const me = result.error;
          const errText =
            (me && (me.error_user_msg || me.message)) || result.message || "Create failed";
          toast.error(errText);
          if (result.hint) toast.info(result.hint, { autoClose: 8000 });
        }
      })
      .catch(() => {
        setTplCreating(false);
        toast.error("Network error");
      });
  };

  const handleBulkSend = async (e) => {
    e.preventDefault();
    if (!appId) return;

    const bulkHeaderFmt = templateHeaderFormat(bulkSelectedRow);
    // Template may already carry a saved default header media (set once at template-create
    // time) - the backend falls back to it automatically (ensureHeaderImageComponent) when no
    // override is given here, so only block when there's neither an override nor a default.
    if (
      isFileHeaderFormat(bulkHeaderFmt) &&
      !bulkHeaderImageFile &&
      !bulkHeaderImageUrl.trim() &&
      bulkHeaderCheck.status !== "ok"
    ) {
      toast.error(bulkHeaderCheck.reason || "This template needs header media — upload a file or paste a public HTTPS URL");
      return;
    }
    if (bulkHeaderFmt === "LOCATION" && (!bulkHeaderLat.trim() || !bulkHeaderLng.trim())) {
      toast.error("This template needs a location header — enter latitude and longitude");
      return;
    }

    const bulkCategory = String(
      (bulkSelectedRow && bulkSelectedRow.category) ||
        (templateRows.find((r) => r && String(r.name) === String(bulkTemplateName).trim()) || {})
          .category ||
        ""
    )
      .trim()
      .toUpperCase();
    const body = {
      app_id: appId,
      templateName: bulkTemplateName.trim() || "hello_world",
      languageCode: bulkLanguageCode.trim() || "en_US",
      ...(bulkBodyParams.trim() ? { body_parameters: bulkBodyParams.trim() } : {}),
      ...(bulkButtonParam.trim() ? { button_url_param: bulkButtonParam.trim() } : {}),
      ...(bulkCopyCodeParam.trim() ? { button_copy_code: bulkCopyCodeParam.trim() } : {}),
      ...(bulkCategory ? { template_category: bulkCategory } : {}),
    };

    if (bulkAudience === "contacts") {
      const lines = [...new Set((bulkRecipientSelect || []).map((o) => o.value))];
      if (!lines.length) {
        toast.error("Add at least one recipient — select a contact or type a number");
        return;
      }
      body.recipients = lines;
    } else {
      body.audience_type = bulkAudience;
      if (bulkAudience === "segment") {
        if (!bulkSelectedSegments.length) {
          toast.error("Select at least one segment");
          return;
        }
        body.segment_ids = bulkSelectedSegments;
      }
      if (!bulkAudienceCount) {
        toast.error("This audience has no reachable WhatsApp numbers");
        return;
      }
    }

    setBulkSending(true);
    setBulkSummary(null);

    try {
      if (bulkHeaderImageFile) {
        body.header_image_id = await uploadWhatsappHeaderMedia(bulkHeaderImageFile, {
          template_category: bulkCategory || undefined,
        });
      } else if (bulkHeaderImageUrl.trim()) {
        body.header_image_url = bulkHeaderImageUrl.trim();
      } else if (bulkHeaderLat.trim() && bulkHeaderLng.trim()) {
        body.header_latitude = bulkHeaderLat.trim();
        body.header_longitude = bulkHeaderLng.trim();
      }
    } catch (err) {
      setBulkSending(false);
      toast.error(err.message || "Header image upload failed");
      return;
    }

    fetchWithAuth(Utils.baseurl + "application/whatsapp/send-bulk", {
      method: "POST",
      body: JSON.stringify(body),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setBulkSending(false);
        if (result.status) {
          toast.success(result.message || "Done");
          setBulkSummary(result.data || null);
        } else {
          toast.error(result.message || "Bulk send failed");
          if (result.hint) toast.info(result.hint, { autoClose: 8000 });
        }
      })
      .catch(() => {
        setBulkSending(false);
        toast.error("Network error");
      });
  };

  const pageTitle =
    showTemplates
      ? "WhatsApp · Templates"
      : showBulk
      ? "WhatsApp · Send Message"
      : "WhatsApp · Connection";
  const pageBlurb = showBulk
    ? "Recipients: segments, all subscribers, or contacts. Every send queues in the background (~350ms apart). Track history & reports under Campaigns."
    : showConnection
    ? "One-time setup: paste your Phone number ID, WABA ID, and an access token from Meta. Your token is stored encrypted."
    : "";

  const handleExportTemplates = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { status, message } = await exportListCsv("application/whatsapp/templates/list", {
        app_id: appId,
        status: appliedTemplateFilterStatus || undefined,
        category: appliedTemplateFilterCategory || undefined,
      }, "template-records.csv");
      if (!status) {
        toast.error(message);
        return;
      }
      if (message) toast.info(message);
    } catch (error) {
      console.error(error);
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_inner">
        {showTemplates ? (
          <div className="dashboard_right_top">
            <div className="dashboard_heading">
              <h1>{showCreateTemplateModal ? "Create template" : "Template Records"}</h1>
            </div>
            <div
              className="add_app wa-template-records-toolbar"
              style={{ gap: "10px", display: showCreateTemplateModal ? "none" : undefined }}
            >
              {can("whatsapp.templates.manage") ? (
              <button
                type="button"
                className="add_app_btn_secondary btn_theme_secondary wa-template-toolbar-delete"
                onClick={handleDeleteSelectedTemplates}
                disabled={
                  templateListLoading || templateBulkDeleting || selectedTemplateIds.length === 0
                }
                title="Delete selected templates from Meta"
              >
                <MdDeleteOutline />
                <span>
                  {templateBulkDeleting
                    ? "Deleting…"
                    : selectedTemplateIds.length > 0
                      ? `Delete (${selectedTemplateIds.length})`
                      : "Delete selected"}
                </span>
              </button>
              ) : null}
              <button
                type="button"
                className="add_app_btn_secondary btn_theme_secondary"
                onClick={() => loadTemplates(true)}
                disabled={templateListLoading || templateBulkDeleting}
                title="Reload templates from Meta"
              >
                <FiRefreshCw />
                <span>{templateListLoading ? "Loading…" : "Refresh"}</span>
              </button>
              <button
                type="button"
                className="add_app_btn_secondary btn_theme_secondary"
                onClick={handleExportTemplates}
                disabled={templateListLoading || templateBulkDeleting || isExporting}
                title="Export CSV for applied filters"
              >
                <MdFileDownload />
                <span>{isExporting ? "Exporting…" : "Export"}</span>
              </button>
              {can("whatsapp.templates.manage") ? (
              <button
                type="button"
                className="add_app_btn btn_theme_primary"
                onClick={() => setShowCreateTemplateModal(true)}
              >
                <MdAddCircle />
                <span>Add Template</span>
              </button>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="dashboard_right_top">
            <div className="dashboard_heading">
              <h1>{pageTitle}</h1>
              <p className="text-muted mb-0" style={{ fontSize: 14, maxWidth: 720, marginTop: 8 }}>
                {pageBlurb}
              </p>
            </div>
          </div>
        )}

        {loading && showConnection ? (
          <p className="text-muted py-4">Loading…</p>
        ) : null}
        {!(loading && showConnection) ? (
          <>
            {showConnection && (
              <div data-embedded-signup-available={embeddedSignup.available ? "1" : "0"}>
                {/* Status banner */}
                <div
                  className="card border-0 shadow-sm mb-3"
                  style={{
                    borderRadius: "0.5rem",
                    borderLeft: `4px solid ${account ? "#25c16f" : "#adb5bd"}`,
                  }}
                >
                  <div className="card-body d-flex flex-wrap align-items-center gap-3 py-3">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: 44,
                        height: 44,
                        flex: "0 0 44px",
                        background: account ? "rgba(37,193,111,.12)" : "#f0f1f3",
                        fontSize: 22,
                      }}
                    >
                      {account ? "✅" : "🔌"}
                    </div>
                    <div className="flex-grow-1">
                      <div className="fw-semibold">
                        {account ? "WhatsApp connected" : "Not connected yet"}
                      </div>
                      <div className="small text-muted">
                        {account ? (
                          <>
                            Phone number ID <strong>{account.phoneNumberId}</strong>
                            {account.businessName ? <> · {account.businessName}</> : null}
                          </>
                        ) : (
                          "Enter your Meta WhatsApp credentials below to start sending."
                        )}
                      </div>
                    </div>
                    {connectionTokenStatus ? (
                      <span
                        className={`badge ${
                          connectionTokenStatus.className === "alert-danger"
                            ? "rounded-pill bg-light-danger"
                            : connectionTokenStatus.className === "alert-warning"
                              ? "rounded-pill bg-light-warning"
                              : "rounded-pill bg-light-success"
                        }`}
                        title={connectionTokenStatus.text}
                      >
                        {connectionTokenStatus.className === "alert-danger"
                          ? "Token expired"
                          : connectionTokenStatus.className === "alert-warning"
                            ? "Token expiring soon"
                            : "Token valid"}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="row g-3">
                  {/* Form */}
                  <div className="col-lg-7">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "0.5rem" }}>
                      <div className="card-body">
                        <h2 className="h6 mb-3">Meta credentials</h2>
                        <form onSubmit={handleSave}>
                          <div className="mb-3">
                            <label className="form-label">
                              Phone number ID <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={phoneNumberId}
                              onChange={(ev) => setPhoneNumberId(ev.target.value)}
                              placeholder="e.g. 112232408762XXXX"
                              required
                              autoComplete="off"
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">WhatsApp Business Account ID (WABA)</label>
                            <input
                              type="text"
                              className="form-control"
                              value={wabaId}
                              onChange={(ev) => setWabaId(ev.target.value)}
                              placeholder="e.g. 833907586398XXX"
                              autoComplete="off"
                            />
                            <div className="form-text">Required for the Templates page.</div>
                          </div>
                          <div className="mb-3">
                            <label className="form-label">
                              Access token {account ? "" : <span className="text-danger">*</span>}
                            </label>
                            <input
                              type="password"
                              className="form-control"
                              value={accessToken}
                              onChange={(ev) => setAccessToken(ev.target.value)}
                              placeholder={account ? "Leave blank to keep existing token" : "Paste your Meta token"}
                              autoComplete="new-password"
                            />
                            <div className="form-text">
                              Stored encrypted, never shown again.
                              {metaTokenExchangeEnabled
                                ? " Temporary tokens auto-upgrade to long-lived (~60 days) on save."
                                : ""}
                            </div>
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Display name (optional)</label>
                            <input
                              type="text"
                              className="form-control"
                              value={businessName}
                              onChange={(ev) => setBusinessName(ev.target.value)}
                              placeholder="Shown in this dashboard only"
                              autoComplete="off"
                            />
                          </div>
                          {can("whatsapp.connection.manage") ? (
                          <button type="submit" className="add_app_btn btn_theme_primary" disabled={saving}>
                            {saving ? "Saving…" : account ? "Update connection" : "Connect WhatsApp"}
                          </button>
                          ) : null}
                        </form>
                      </div>
                    </div>
                  </div>

                  {/* Help */}
                  <div className="col-lg-5">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "0.5rem", background: "#f8f9fb" }}>
                      <div className="card-body">
                        <h2 className="h6 mb-3">Where to find these</h2>
                        <ol className="small text-muted ps-3 mb-3" style={{ lineHeight: 1.7 }}>
                          <li>
                            Open{" "}
                            <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer">
                              Meta for Developers
                            </a>{" "}
                            → your app → <strong>WhatsApp → API Setup</strong>.
                          </li>
                          <li>
                            Copy the <strong>Phone number ID</strong> and{" "}
                            <strong>WhatsApp Business Account ID</strong> shown there.
                          </li>
                          <li>
                            Click <strong>Generate access token</strong> and paste it here.
                          </li>
                        </ol>
                        <div className="small text-muted" style={{ borderTop: "1px solid #e6e8ec", paddingTop: 12 }}>
                          <strong>Production tip:</strong> API Setup tokens last only a few hours. Use a{" "}
                          <strong>System User token</strong> from Business Manager — it never expires.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showTemplates && (
              <>
                {!showCreateTemplateModal && (
                <>
                <div className="list-page-filter-card">
                  <button
                    type="button"
                    className="list-page-filter-accordion-header"
                    onClick={() => setTemplateFilterAccordionOpen((o) => !o)}
                    aria-expanded={templateFilterAccordionOpen}
                    aria-controls="whatsapp-templates-filter-panel"
                    id="whatsapp-templates-filter-accordion"
                  >
                    <span className="list-page-filter-accordion-title">Filter</span>
                    <MdKeyboardArrowDown
                      className={`list-page-filter-accordion-chevron ${
                        templateFilterAccordionOpen ? "list-page-filter-accordion-chevron--open" : ""
                      }`}
                      size={22}
                      aria-hidden
                    />
                  </button>
                  <Collapse in={templateFilterAccordionOpen}>
                    <div
                      className="list-page-filter-accordion-body"
                      id="whatsapp-templates-filter-panel"
                      role="region"
                      aria-labelledby="whatsapp-templates-filter-accordion"
                    >
                      <div className="list-page-filter-bar">
                        <div className="list-page-filter-fields row g-3 align-items-end">
                          <div className="list-page-filter-field col-12 col-lg-4 col-xl-3">
                            <div style={{ minWidth: 0, width: "100%" }}>
                              <input
                                type="text"
                                id="wa-tpl-filter-name"
                                className="form-control"
                                style={{ height: TPL_BTN_FIELD_HEIGHT }}
                                placeholder="Search by template name"
                                aria-label="Template name"
                                value={draftTemplateFilterName}
                                onChange={(e) => setDraftTemplateFilterName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") handleApplyTemplateFilter();
                                }}
                              />
                            </div>
                          </div>
                          <div className="list-page-filter-field col-12 col-lg-4 col-xl-3">
                            <div style={{ minWidth: 0, width: "100%" }}>
                              <Select
                                inputId="wa-tpl-filter-status"
                                instanceId="wa-tpl-filter-status"
                                classNamePrefix="ns-wa-tpl-status"
                                isSearchable
                                isClearable
                                placeholder="Status"
                                aria-label="Status"
                                options={WA_TEMPLATE_STATUS_FILTER_OPTIONS}
                                value={draftTemplateStatusSelectValue}
                                onChange={(opt) =>
                                  setDraftTemplateFilterStatus(opt?.value ?? "")
                                }
                                styles={templateStatusSelectStyles}
                                menuPortalTarget={
                                  typeof document !== "undefined" ? document.body : null
                                }
                                menuPosition="fixed"
                              />
                            </div>
                          </div>
                          <div className="list-page-filter-field col-12 col-lg-4 col-xl-3">
                            <div style={{ minWidth: 0, width: "100%" }}>
                              <Select
                                inputId="wa-tpl-filter-category"
                                instanceId="wa-tpl-filter-category"
                                classNamePrefix="ns-wa-tpl-category"
                                isSearchable
                                isClearable
                                placeholder="Category"
                                aria-label="Category"
                                options={WA_TEMPLATE_CATEGORY_FILTER_OPTIONS}
                                value={draftTemplateCategorySelectValue}
                                onChange={(opt) =>
                                  setDraftTemplateFilterCategory(opt?.value ?? "")
                                }
                                styles={templateStatusSelectStyles}
                                menuPortalTarget={
                                  typeof document !== "undefined" ? document.body : null
                                }
                                menuPosition="fixed"
                              />
                            </div>
                          </div>
                          <div className="list-page-filter-field list-page-filter-actions col-12 col-lg-auto ms-lg-auto">
                            <div
                              className="add_app"
                              style={{ justifyContent: "flex-end", width: "100%" }}
                            >
                              <button
                                type="button"
                                className="add_app_btn btn_theme_primary"
                                title="Apply filter"
                                onClick={handleApplyTemplateFilter}
                              >
                                <MdSearch />
                                <span>Search</span>
                              </button>
                              <button
                                type="button"
                                className="add_app_btn_secondary btn_theme_secondary"
                                title="Reset filter"
                                onClick={handleResetTemplateFilter}
                              >
                                <MdRefresh />
                                <span>Reset</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Collapse>
                </div>

                <div className="dashboard_app_table dashboard_app_table--list-records">
                  <div className="dashboard_table">
                    <div
                      ref={templateTableScrollRef}
                      className="table_scroll_wrapper"
                    >
                      <div
                        ref={templateTableInnerRef}
                        className={`table_inner table_y_scroll ${isAtScrollEnd ? "at-scroll-end" : ""}`}
                        style={{
                          maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : "calc(100vh - 320px)",
                          overflowY: "auto",
                          overflowX: "auto",
                        }}
                      >
                          <table className="table_outer table-sm">
                            <thead>
                              <tr>
                                <th style={{ width: 40 }} className="text-center">
                                  <input
                                    ref={templateSelectAllRef}
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={
                                      filteredTemplateRowsWithId.length > 0 &&
                                      filteredTemplateRowsWithId.every((r) =>
                                        selectedTemplateIds.includes(String(r.id))
                                      )
                                    }
                                    onChange={toggleSelectAllTemplates}
                                    disabled={
                                      templateListLoading ||
                                      templateBulkDeleting ||
                                      filteredTemplateRowsWithId.length === 0
                                    }
                                    title="Select all"
                                    aria-label="Select all templates"
                                  />
                                </th>
                                <th>Name</th>
                                <th>Preview</th>
                                <th>Language</th>
                                <th>Status</th>
                                <th>Category</th>
                                <th className="col-action" style={{ textAlign: "center" }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {templateListLoading && !templateRows.length ? (
                                <tr>
                                  <td colSpan={7} className="text-center text-muted">
                                    Loading…
                                  </td>
                                </tr>
                              ) : templateRows.length === 0 ? (
                                <tr>
                                  <td colSpan={7} className="text-center text-muted">
                                    No templates returned. Check WABA ID, token scopes, and Connection.
                                  </td>
                                </tr>
                              ) : filteredTemplateRows.length === 0 ? (
                                <tr>
                                  <td colSpan={7} className="text-center text-muted">
                                    No templates match this filter.
                                  </td>
                                </tr>
                              ) : (
                                filteredTemplateRows.map((row) => {
                                const categoryDisplay = getWhatsAppTemplateCategoryDisplay(row.category);
                                return (
                                <tr key={`${row.id || row.name}-${row.language || ""}`}>
                                  <td className="text-center">
                                    {row.id ? (
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={selectedTemplateIds.includes(String(row.id))}
                                        onChange={() => toggleTemplateSelected(row)}
                                        disabled={templateListLoading || templateBulkDeleting}
                                        aria-label={`Select ${row.name || "template"}`}
                                      />
                                    ) : (
                                      <span className="text-muted">—</span>
                                    )}
                                  </td>
                                  <td>
                                    <span className="fw-medium">{row.name}</span>
                                  </td>
                                  <td
                                    className="small text-muted"
                                    style={{ maxWidth: 260, whiteSpace: "normal" }}
                                    title={templateBodyPreview(row)}
                                  >
                                    {templateBodyPreview(row)}
                                  </td>
                                  <td>{row.language}</td>
                                  <td>{templateStatusBadge(row)}</td>
                                  <td>
                                    <span className={categoryDisplay.badgeClass}>
                                      {categoryDisplay.label}
                                    </span>
                                  </td>
                                  <td className="col-action" style={{ textAlign: "center" }}>
                                    <WaTemplateRowActionsToggle
                                      onClick={(e) => handleOpenTemplateActions(e, row)}
                                      id={`wa-tpl-actions-${row.id || row.name}`}
                                    />
                                  </td>
                                </tr>
                                );
                              })
                              )}
                            </tbody>
                          </table>
                        </div>
                    </div>
                    {openTemplateActionId && templateDropdownPosition
                      ? createPortal(
                          <div
                            ref={templateDropdownRef}
                            style={{
                              position: "fixed",
                              left: templateDropdownPosition.left,
                              top: templateDropdownPosition.top,
                              zIndex: 999999,
                              minWidth: "220px",
                            }}
                          >
                            <div className="dropdown-menu-subscription">
                              {can("whatsapp.templates.manage") ? (
                              <button
                                type="button"
                                className="delete-class"
                                disabled={
                                  templateListLoading ||
                                  templateBulkDeleting ||
                                  !filteredTemplateRows.find(
                                    (r) =>
                                      String(r.id || `${r.name || ""}-${r.language || ""}`) ===
                                      openTemplateActionId
                                  )?.id ||
                                  templateDeletingId ===
                                    String(
                                      filteredTemplateRows.find(
                                        (r) =>
                                          String(r.id || `${r.name || ""}-${r.language || ""}`) ===
                                          openTemplateActionId
                                      )?.id || ""
                                    )
                                }
                                onClick={() => {
                                  const row = filteredTemplateRows.find(
                                    (r) =>
                                      String(r.id || `${r.name || ""}-${r.language || ""}`) ===
                                      openTemplateActionId
                                  );
                                  closeTemplateActionsDropdown();
                                  if (row) handleDeleteTemplate(row);
                                }}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-start",
                                  textAlign: "left",
                                }}
                              >
                                {(() => {
                                  const row = filteredTemplateRows.find(
                                    (r) =>
                                      String(r.id || `${r.name || ""}-${r.language || ""}`) ===
                                      openTemplateActionId
                                  );
                                  return templateDeletingId === String(row?.id || "")
                                    ? "Deleting…"
                                    : "Delete";
                                })()}
                              </button>
                              ) : null}
                            </div>
                          </div>,
                          document.body
                        )
                      : null}
                  </div>
                </div>
                </>
                )}

                {showCreateTemplateModal && (
                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "0.5rem" }}>
                  <div className="card-body">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => !tplCreating && !libCreating && setShowCreateTemplateModal(false)}
                      >
                        ← Back to templates
                      </button>
                    </div>
                    <div className="btn-group w-100 mb-3" role="tablist">
                      <button
                        type="button"
                        className={`btn btn-sm ${tplCreateMode === "custom" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setTplCreateMode("custom")}
                      >
                        Build your own
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${tplCreateMode === "library" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => {
                          setTplCreateMode("library");
                          if (!libRows.length && !libLoading) loadLibraryTemplates();
                        }}
                      >
                        Meta template library (instant approval)
                      </button>
                    </div>
                    {tplCreateMode === "library" ? (
                      <div>
                        <p className="small text-muted mb-3">
                          Pre-approved templates from Meta — created instantly, <strong>no review wait</strong>. Pick one,
                          give it a name, done.
                        </p>
                        <div className="row g-2 mb-3">
                          <div className="col-md-6">
                            <input
                              type="text"
                              className="form-control"
                              value={libSearch}
                              onChange={(ev) => setLibSearch(ev.target.value)}
                              onKeyDown={(ev) => {
                                if (ev.key === "Enter") {
                                  ev.preventDefault();
                                  loadLibraryTemplates();
                                }
                              }}
                              placeholder="Search e.g. order, appointment, payment…"
                            />
                          </div>
                          <div className="col-md-3">
                            <select
                              className="form-select"
                              value={libCategory}
                              onChange={(ev) => setLibCategory(ev.target.value)}
                            >
                              <option value="UTILITY">UTILITY</option>
                              <option value="AUTHENTICATION">AUTHENTICATION</option>
                              <option value="">All categories</option>
                            </select>
                          </div>
                          <div className="col-md-3">
                            <button
                              type="button"
                              className="btn btn-outline-secondary w-100"
                              onClick={loadLibraryTemplates}
                              disabled={libLoading}
                            >
                              {libLoading ? "Loading…" : "Browse"}
                            </button>
                          </div>
                        </div>
                        {!libBrowseSupported && !libLoading ? (
                          <div className="border rounded p-3 mb-3" style={{ background: "rgba(13,110,253,.04)" }}>
                            <p className="small mb-2">
                              Meta doesn&apos;t allow browsing the library through the API for this account — but
                              creating from it still works. Open{" "}
                              <a
                                href="https://business.facebook.com/latest/whatsapp_manager/template_library"
                                target="_blank"
                                rel="noreferrer"
                              >
                                Meta&apos;s Template Library
                              </a>
                              , pick a template, copy its <strong>name</strong> (shown under the card, e.g.{" "}
                              <code>address_update</code>), and paste it here — it&apos;s created instantly.
                            </p>
                            <div className="row g-2 mb-2">
                              <div className="col-md-6">
                                <label className="form-label small mb-1">Library template name (from Meta)</label>
                                <input
                                  type="text"
                                  className="form-control font-monospace"
                                  value={libManualName}
                                  onChange={(ev) => setLibManualName(ev.target.value)}
                                  placeholder="e.g. address_update"
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label small mb-1">Your template name</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={libNewName}
                                  onChange={(ev) => setLibNewName(ev.target.value)}
                                  placeholder="defaults to library name"
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label small mb-1">
                                  Your website URL (only if template has a link button)
                                </label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={libButtonBaseUrl}
                                  onChange={(ev) => setLibButtonBaseUrl(ev.target.value)}
                                  placeholder="https://yourdomain.com"
                                />
                              </div>
                            </div>
                            {can("whatsapp.templates.manage") ? (
                            <button
                              type="button"
                              className="add_app_btn btn_theme_primary"
                              disabled={libCreating || !libManualName.trim()}
                              onClick={() =>
                                handleCreateFromLibrary({ name: libManualName.trim(), category: libCategory })
                              }
                            >
                              {libCreating ? "Creating…" : "Create — instant approval"}
                            </button>
                            ) : null}
                          </div>
                        ) : libLoading ? (
                          <p className="text-muted small">Loading library…</p>
                        ) : libRows.length === 0 ? (
                          <p className="text-muted small">
                            No results. Try a search word like &quot;order&quot; or &quot;reminder&quot;, or press Browse.
                          </p>
                        ) : (
                          <div style={{ maxHeight: 320, overflow: "auto" }} className="mb-3">
                            {libRows.map((t, i) => (
                              <div
                                key={`${t.name}-${i}`}
                                className={`border rounded p-2 mb-2 ${libPick && libPick.name === t.name ? "border-primary bg-light" : ""}`}
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  setLibPick(t);
                                  setLibNewName(t.name);
                                }}
                              >
                                <div className="d-flex justify-content-between align-items-center">
                                  <strong className="small">{t.name}</strong>
                                  <span className="badge bg-secondary">{t.category || libCategory}</span>
                                </div>
                                {t.topic || t.usecase ? (
                                  <div className="small text-muted">
                                    {[t.topic, t.usecase].filter(Boolean).join(" · ")}
                                  </div>
                                ) : null}
                                <div className="small mt-1" style={{ whiteSpace: "pre-wrap" }}>
                                  {t.body || ""}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {libPick ? (
                          <div className="border-top pt-3">
                            <div className="row g-2 mb-2">
                              <div className="col-md-6">
                                <label className="form-label small mb-1">Your template name</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={libNewName}
                                  onChange={(ev) => setLibNewName(ev.target.value)}
                                />
                              </div>
                              {Array.isArray(libPick.buttons) &&
                              libPick.buttons.some((b) => String(b.type || "").toUpperCase() === "URL") ? (
                                <div className="col-md-6">
                                  <label className="form-label small mb-1">Your website URL (for the button)</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={libButtonBaseUrl}
                                    onChange={(ev) => setLibButtonBaseUrl(ev.target.value)}
                                    placeholder="https://yourdomain.com"
                                  />
                                </div>
                              ) : null}
                            </div>
                            {can("whatsapp.templates.manage") ? (
                            <button
                              type="button"
                              className="add_app_btn btn_theme_primary"
                              onClick={handleCreateFromLibrary}
                              disabled={libCreating}
                            >
                              {libCreating ? "Creating…" : "Create — instant approval"}
                            </button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <>
                    <div className="row g-4">
                      <div className="col-lg-7">
                        <label className="form-label fw-semibold">Category</label>
                        <div className="row g-2 mb-3">
                          {[
                            { v: "MARKETING", t: "Marketing", d: "Offers, promotions, announcements", icon: "📣" },
                            { v: "UTILITY", t: "Utility", d: "Order updates, reminders, account info", icon: "🔔" },
                            { v: "AUTHENTICATION", t: "Authentication", d: "OTP codes — Meta's preset layout", icon: "🔑" },
                          ].map((c) => (
                            <div className="col-4" key={c.v}>
                              <div
                                role="button"
                                tabIndex={0}
                                onClick={() => setCreateTplCategory(c.v)}
                                onKeyDown={(ev) => {
                                  if (ev.key === "Enter" || ev.key === " ") setCreateTplCategory(c.v);
                                }}
                                className={`border rounded p-2 h-100 ${
                                  createTplCategory === c.v ? "border-primary" : "border-light-subtle"
                                }`}
                                style={{
                                  cursor: "pointer",
                                  background: createTplCategory === c.v ? "rgba(13,110,253,.06)" : "#fff",
                                  boxShadow: createTplCategory === c.v ? "0 0 0 1px #0d6efd inset" : "none",
                                }}
                              >
                                <div className="fw-semibold small">
                                  {c.icon} {c.t}
                                </div>
                                <div className="text-muted" style={{ fontSize: 12 }}>
                                  {c.d}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                    <form id="whatsapp-create-template-form" onSubmit={handleCreateTemplate}>
                      <div className="row g-3 mb-3">
                        <div className="col-md-7">
                          <label className="form-label">Template name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={createTplName}
                            onChange={(ev) => setCreateTplName(ev.target.value)}
                            placeholder="e.g. order_shipped"
                            required={createTplCategory === "AUTHENTICATION"}
                          />
                          <div className="form-text">Lowercase and underscores only, must be unique.</div>
                        </div>
                        <div className="col-md-5">
                          <label className="form-label">Language</label>
                          <input
                            type="text"
                            className="form-control"
                            value={createTplLang}
                            onChange={(ev) => setCreateTplLang(ev.target.value)}
                            placeholder="en_US"
                          />
                        </div>
                      </div>
                      {createTplCategory === "AUTHENTICATION" ? (
                        <>
                          <div className="mb-3">
                            <label className="form-label fw-semibold">Code delivery</label>
                            {[
                              {
                                v: "ZERO_TAP",
                                t: "Zero-tap auto-fill (recommended)",
                                d: "Code is handed to your Android app without any tap. Needs package + signature hash + app handshake before send.",
                              },
                              {
                                v: "ONE_TAP",
                                t: "One-tap autofill",
                                d: "User taps Autofill — code opens your Android app. Needs package + signature hash.",
                              },
                              {
                                v: "COPY_CODE",
                                t: "Copy code",
                                d: "Customer taps a button to copy the code — works everywhere, no app setup.",
                              },
                            ].map((o) => (
                              <div key={o.v}>
                                <div className="form-check">
                                  <input
                                    id={`auth-otp-${o.v}`}
                                    type="radio"
                                    name="auth-otp-type"
                                    className="form-check-input"
                                    checked={authOtpType === o.v}
                                    onChange={() => {
                                      setAuthOtpType(o.v);
                                      if (o.v !== "ZERO_TAP") setAuthZeroTapTermsAccepted(false);
                                    }}
                                  />
                                  <label className="form-check-label" htmlFor={`auth-otp-${o.v}`}>
                                    <span className="fw-medium">{o.t}</span>{" "}
                                    <span className="text-muted small">— {o.d}</span>
                                  </label>
                                </div>
                                {o.v === "ZERO_TAP" && authOtpType === "ZERO_TAP" ? (
                                  <div className="form-check ms-4 mt-2 mb-2">
                                    <input
                                      id="auth-zero-tap-terms"
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={authZeroTapTermsAccepted}
                                      onChange={(ev) => setAuthZeroTapTermsAccepted(ev.target.checked)}
                                    />
                                    <label className="form-check-label small" htmlFor="auth-zero-tap-terms">
                                      By selecting zero-tap, I understand that{" "}
                                      <strong>{businessName.trim() || "this business"}</strong>
                                      &apos;s use of zero-tap authentication is subject to the WhatsApp Business
                                      Terms of Service. It is this business&apos;s responsibility to ensure that
                                      its customers expect that the code will be automatically filled in on their
                                      behalf when they choose to receive the zero-tap code through WhatsApp.
                                    </label>
                                  </div>
                                ) : null}
                              </div>
                            ))}
                          </div>
                          {authOtpType !== "COPY_CODE" ? (
                            <div className="mb-3">
                              <label className="form-label fw-semibold">App setup</label>
                              <div className="form-text mb-2">
                                Same as WhatsApp Manager — add debug + release signing hashes (or multiple apps).
                              </div>
                              {authSupportedApps.map((row, idx) => (
                                <div className="row g-2 mb-2 align-items-end" key={`auth-app-${idx}`}>
                                  <div className="col-md-5">
                                    <label className="form-label small mb-1">Package name</label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={row.package_name}
                                      onChange={(ev) => {
                                        const next = [...authSupportedApps];
                                        next[idx] = { ...next[idx], package_name: ev.target.value };
                                        setAuthSupportedApps(next);
                                      }}
                                      placeholder="com.yourapp.package"
                                    />
                                  </div>
                                  <div className="col-md-5">
                                    <label className="form-label small mb-1">Signature hash (11 chars)</label>
                                    <input
                                      type="text"
                                      className="form-control font-monospace"
                                      value={row.signature_hash}
                                      onChange={(ev) => {
                                        const next = [...authSupportedApps];
                                        next[idx] = { ...next[idx], signature_hash: ev.target.value };
                                        setAuthSupportedApps(next);
                                      }}
                                      maxLength={11}
                                      placeholder="e.g. K8a/AINcGX7"
                                    />
                                  </div>
                                  <div className="col-md-2">
                                    {authSupportedApps.length > 1 ? (
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm w-100"
                                        onClick={() =>
                                          setAuthSupportedApps(authSupportedApps.filter((_, i) => i !== idx))
                                        }
                                      >
                                        Remove
                                      </button>
                                    ) : null}
                                  </div>
                                </div>
                              ))}
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() =>
                                  setAuthSupportedApps([
                                    ...authSupportedApps,
                                    { package_name: "", signature_hash: "" },
                                  ])
                                }
                              >
                                + Add another app
                              </button>
                              <div className="row g-3 mt-1">
                                <div className="col-md-6">
                                  <label className="form-label">Autofill button text</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={authAutofillText}
                                    onChange={(ev) => setAuthAutofillText(ev.target.value)}
                                    maxLength={25}
                                    placeholder="Autofill (optional)"
                                  />
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <div className="form-check mb-3">
                            <input
                              id="auth-sec"
                              type="checkbox"
                              className="form-check-input"
                              checked={authAddSecurityRecommendation}
                              onChange={(ev) => setAuthAddSecurityRecommendation(ev.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="auth-sec">
                              Include security line (&quot;For your security, do not share this code&quot;)
                            </label>
                          </div>
                          <div className="row g-3 mb-3">
                            <div className="col-md-4">
                              <label className="form-label">Code expires in (minutes)</label>
                              <input
                                type="number"
                                className="form-control"
                                min={1}
                                max={90}
                                value={authCodeExpirationMinutes}
                                onChange={(ev) => setAuthCodeExpirationMinutes(ev.target.value)}
                                placeholder="e.g. 10"
                              />
                            </div>
                            <div className="col-md-4">
                              <label className="form-label">Copy-code button text</label>
                              <input
                                type="text"
                                className="form-control"
                                value={authCopyButtonText}
                                onChange={(ev) => setAuthCopyButtonText(ev.target.value)}
                                maxLength={25}
                                placeholder="Copy Code (optional)"
                              />
                            </div>
                            <div className="col-md-4">
                              <label className="form-label">Message TTL (seconds)</label>
                              <input
                                type="number"
                                className="form-control"
                                min={30}
                                max={600}
                                value={authMessageTtlSeconds}
                                onChange={(ev) => setAuthMessageTtlSeconds(ev.target.value)}
                                placeholder="e.g. 60"
                              />
                              <div className="form-text">Optional; Meta may reject on some accounts.</div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="mb-3">
                            <label className="form-label d-block mb-2">Media sample (optional)</label>
                            <select
                              className="form-select"
                              value={createTplHeaderType}
                              onChange={(ev) => {
                                setCreateTplHeaderType(ev.target.value);
                                setCreateTplHeaderFile(null);
                              }}
                            >
                              {WA_HEADER_MEDIA_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                            {isFileHeaderFormat(createTplHeaderType) ? (
                              <>
                                <input
                                  type="file"
                                  className="form-control mt-2"
                                  accept={headerFileAccept(createTplHeaderType)}
                                  onChange={(ev) =>
                                    setCreateTplHeaderFile(
                                      ev.target.files && ev.target.files[0] ? ev.target.files[0] : null
                                    )
                                  }
                                />
                                <div className="form-text">
                                  {createTplHeaderType === "VIDEO"
                                    ? "MP4 or 3GPP, max 16 MB. "
                                    : createTplHeaderType === "DOCUMENT"
                                      ? "PDF, max 16 MB. "
                                      : "JPEG or PNG, max 5 MB. "}
                                  Meta uses this only as a sample for review. At send time you pick the real file; it
                                  can be this one or a different one.
                                </div>
                              </>
                            ) : createTplHeaderType === "LOCATION" ? (
                              <div className="form-text mt-2">
                                Location header has no sample file. Latitude and longitude are sent when you message
                                customers.
                              </div>
                            ) : (
                              <input
                                type="text"
                                className="form-control mt-2"
                                value={createTplHeader}
                                onChange={(ev) => setCreateTplHeader(ev.target.value)}
                                maxLength={60}
                                placeholder="Optional text header (max 60 characters)"
                              />
                            )}
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Body</label>
                            <div className="wa-tpl-body-wrap">
                              <textarea
                                ref={createTplBodyRef}
                                className="form-control"
                                rows={8}
                                value={createTplBody}
                                onChange={(ev) => setCreateTplBody(ev.target.value)}
                                placeholder="Message body. Variables: {{1}}, {{2}}, …  Use *bold* _italic_ ~strike~ ```mono```"
                                required
                              />
                              <span className="wa-tpl-body-count">
                                {createTplBody.length}/{WA_TPL_BODY_MAX}
                              </span>
                            </div>
                            <div className="wa-tpl-body-toolbar">
                              <button
                                type="button"
                                title="Bold"
                                aria-label="Bold"
                                onClick={() => applyTplBodyWrap("*", "*")}
                              >
                                <strong>B</strong>
                              </button>
                              <button
                                type="button"
                                title="Italic"
                                aria-label="Italic"
                                onClick={() => applyTplBodyWrap("_", "_")}
                              >
                                <em>I</em>
                              </button>
                              <button
                                type="button"
                                title="Strikethrough"
                                aria-label="Strikethrough"
                                onClick={() => applyTplBodyWrap("~", "~")}
                              >
                                <s>S</s>
                              </button>
                              <button
                                type="button"
                                title="Monospace"
                                aria-label="Monospace"
                                onClick={() => applyTplBodyWrap("```", "```")}
                              >
                                {"</>"}
                              </button>
                              <button
                                type="button"
                                className="wa-tpl-body-toolbar-addvar"
                                title="Add variable"
                                onClick={insertTplBodyVariable}
                              >
                                + Add variable
                              </button>
                            </div>
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Footer (optional)</label>
                            <input
                              type="text"
                              className="form-control"
                              value={createTplFooter}
                              onChange={(ev) => setCreateTplFooter(ev.target.value)}
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Example values for variables (optional)</label>
                            <input
                              type="text"
                              className="form-control"
                              value={createTplExample}
                              onChange={(ev) => setCreateTplExample(ev.target.value)}
                              placeholder="e.g. John, #12345 for {{1}}, {{2}}"
                            />
                          </div>
                          <div className="mb-3">
                            <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-2">
                              <div>
                                <label className="form-label mb-1">Buttons · Optional</label>
                                <div className="form-text mb-0">
                                  Create buttons that let customers respond to your message or take action. You can
                                  add up to ten buttons. If you add more than three buttons, they will appear in a
                                  list.
                                </div>
                              </div>
                              <Dropdown align="end">
                                <Dropdown.Toggle
                                  variant="outline-secondary"
                                  size="sm"
                                  id="wa-tpl-add-button"
                                  disabled={createTplButtons.length >= 10}
                                >
                                  + Add button
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  {WA_TEMPLATE_BUTTON_TYPES.map((opt) => (
                                    <Dropdown.Item
                                      key={opt.value}
                                      onClick={() =>
                                        setCreateTplButtons((prev) => [
                                          ...prev,
                                          { type: opt.value, ...(opt.defaults || {}) },
                                        ])
                                      }
                                    >
                                      {opt.label}
                                    </Dropdown.Item>
                                  ))}
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                            {createTplButtons.length ? (
                              <div className="mb-1 small text-muted">Call to action · Optional</div>
                            ) : null}
                            {createTplButtons.map((btn, idx) => {
                              const patchBtn = (patch) =>
                                setCreateTplButtons((prev) =>
                                  prev.map((b, i) => (i === idx ? { ...b, ...patch } : b))
                                );
                              const typeValue =
                                findOptionByValue(WA_TEMPLATE_BUTTON_TYPES, Number(btn.type)) ||
                                WA_TEMPLATE_BUTTON_TYPES[0];
                              const apiType = typeValue.apiType;
                              const urlDynamic = String(btn.urlType || "").toLowerCase() === "dynamic";
                              return (
                                <div key={`tpl-btn-${idx}`} className="border rounded p-3 mb-2 bg-white">
                                  <div className="ns-wa-tpl-btn-row">
                                    <div className="ns-wa-tpl-btn-field ns-wa-tpl-btn-field--type">
                                      <label className="form-label small mb-1">Type of action</label>
                                      <Select
                                        inputId={`wa-tpl-btn-type-${idx}`}
                                        instanceId={`wa-tpl-btn-type-${idx}`}
                                        classNamePrefix="ns-wa-tpl-btn-type"
                                        isSearchable={false}
                                        options={WA_TEMPLATE_BUTTON_TYPES}
                                        value={typeValue}
                                        onChange={(opt) => {
                                          const next = findOptionByValue(
                                            WA_TEMPLATE_BUTTON_TYPES,
                                            Number(opt?.value)
                                          );
                                          if (!next) return;
                                          setCreateTplButtons((prev) =>
                                            prev.map((b, i) =>
                                              i === idx
                                                ? { type: next.value, ...(next.defaults || {}) }
                                                : b
                                            )
                                          );
                                        }}
                                        styles={templateStatusSelectStyles}
                                        menuPortalTarget={
                                          typeof document !== "undefined" ? document.body : null
                                        }
                                        menuPosition="fixed"
                                      />
                                    </div>
                                    {apiType !== "COPY_CODE" ? (
                                      <div className="ns-wa-tpl-btn-field ns-wa-tpl-btn-field--text">
                                        <label className="form-label small mb-1">
                                          Button text{" "}
                                          <span className="text-muted">
                                            ({String(btn.text || "").length}/25)
                                          </span>
                                        </label>
                                        <input
                                          type="text"
                                          className="form-control"
                                          value={btn.text || ""}
                                          maxLength={25}
                                          placeholder="Button label"
                                          onChange={(ev) => patchBtn({ text: ev.target.value })}
                                        />
                                      </div>
                                    ) : null}
                                    {apiType === "URL" ? (
                                      <>
                                        <div className="ns-wa-tpl-btn-field ns-wa-tpl-btn-field--urltype">
                                          <label className="form-label small mb-1">URL type</label>
                                          <select
                                            className="form-select"
                                            value={urlDynamic ? "dynamic" : "static"}
                                            onChange={(ev) =>
                                              patchBtn({
                                                urlType: ev.target.value,
                                                example: ev.target.value === "static" ? "" : btn.example,
                                              })
                                            }
                                          >
                                            <option value="static">Static</option>
                                            <option value="dynamic">Dynamic</option>
                                          </select>
                                        </div>
                                        <div className="ns-wa-tpl-btn-field ns-wa-tpl-btn-field--grow">
                                          <label className="form-label small mb-1">
                                            Website URL{" "}
                                            <span className="text-muted">
                                              ({String(btn.url || "").length}/2000)
                                            </span>
                                          </label>
                                          <input
                                            type="text"
                                            className="form-control font-monospace"
                                            value={btn.url || ""}
                                            maxLength={2000}
                                            placeholder="https://"
                                            onChange={(ev) => patchBtn({ url: ev.target.value })}
                                          />
                                        </div>
                                        {urlDynamic ? (
                                          <div className="ns-wa-tpl-btn-field ns-wa-tpl-btn-field--grow">
                                            <label className="form-label small mb-1">URL example</label>
                                            <input
                                              type="text"
                                              className="form-control font-monospace"
                                              value={btn.example || ""}
                                              placeholder="/promo-path or full example URL"
                                              onChange={(ev) => patchBtn({ example: ev.target.value })}
                                              title="Add an example of the website URL. Do not use real customer information."
                                            />
                                          </div>
                                        ) : null}
                                      </>
                                    ) : null}
                                    {apiType === "PHONE_NUMBER" ? (
                                      <div className="ns-wa-tpl-btn-field ns-wa-tpl-btn-field--grow">
                                        <label className="form-label small mb-1">Phone number</label>
                                        <input
                                          type="text"
                                          className="form-control font-monospace"
                                          value={btn.phone_number || ""}
                                          placeholder="+919876543210"
                                          onChange={(ev) => patchBtn({ phone_number: ev.target.value })}
                                        />
                                      </div>
                                    ) : null}
                                    {apiType === "COPY_CODE" ? (
                                      <div className="ns-wa-tpl-btn-field ns-wa-tpl-btn-field--grow">
                                        <label className="form-label small mb-1">
                                          Sample offer code ({String(btn.example || "").length}/15)
                                        </label>
                                        <input
                                          type="text"
                                          className="form-control font-monospace"
                                          value={btn.example || ""}
                                          maxLength={15}
                                          placeholder="e.g. SAVE20"
                                          onChange={(ev) => patchBtn({ example: ev.target.value })}
                                        />
                                      </div>
                                    ) : null}
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger ns-wa-tpl-btn-remove d-inline-flex align-items-center justify-content-center"
                                      title="Remove button"
                                      aria-label="Remove button"
                                      onClick={() =>
                                        setCreateTplButtons((prev) => prev.filter((_, i) => i !== idx))
                                      }
                                    >
                                      <MdDeleteOutline size={20} />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                      {can("whatsapp.templates.manage") ? (
                      <button type="submit" className="add_app_btn btn_theme_primary" disabled={tplCreating}>
                        {tplCreating
                          ? "Submitting…"
                          : createTplCategory === "AUTHENTICATION"
                            ? "Create template"
                            : "Submit for review"}
                      </button>
                      ) : null}
                      {createTplCategory !== "AUTHENTICATION" ? (
                        <div className="form-text mt-2">
                          Goes to Meta for review — usually minutes to a few hours. Track status on the Templates list.
                        </div>
                      ) : (
                        <div className="form-text mt-2">
                          Meta&apos;s preset OTP layout — approved almost instantly. Body text cannot be customised.
                        </div>
                      )}
                    </form>
                      </div>
                      <div className="col-lg-5">
                        {createTplCategory === "AUTHENTICATION" ? (
                        <>
                        <div className="small fw-semibold mb-2 text-muted">Preview</div>
                        <div
                          style={{
                            background: "#e5ddd5",
                            borderRadius: 12,
                            padding: 16,
                            minHeight: 260,
                            backgroundImage:
                              "linear-gradient(rgba(229,221,213,.92), rgba(229,221,213,.92))",
                          }}
                        >
                          <div
                            style={{
                              background: "#fff",
                              borderRadius: "8px 8px 8px 8px",
                              maxWidth: "92%",
                              boxShadow: "0 1px 1px rgba(0,0,0,.12)",
                            }}
                          >
                            <div style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>
                              <strong>123456</strong> is your verification code.
                              {authAddSecurityRecommendation
                                ? " For your security, do not share this code."
                                : ""}
                            </div>
                            {authCodeExpirationMinutes ? (
                              <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                                This code expires in {authCodeExpirationMinutes} minutes.
                              </div>
                            ) : null}
                            <div className="text-end text-muted" style={{ fontSize: 11, marginTop: 4, padding: 8 }}>
                              {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </div>
                          </div>
                          <div
                            className="text-center"
                            style={{
                              background: "#fff",
                              borderRadius: 8,
                              padding: "8px 12px",
                              maxWidth: "92%",
                              marginTop: 2,
                              color: "#0d6efd",
                              fontSize: 14,
                              boxShadow: "0 1px 1px rgba(0,0,0,.12)",
                            }}
                          >
                            {authOtpType === "COPY_CODE"
                              ? `⧉ ${authCopyButtonText.trim() || "Copy code"}`
                              : `⚡ ${authAutofillText.trim() || "Autofill"}`}
                          </div>
                        </div>
                        </>
                        ) : (
                          <WhatsAppMessagePreview
                            bodyText={tplPreviewBody()}
                            footerText={createTplFooter.trim() ? createTplFooter : ''}
                            headerNode={
                              createTplHeaderType === "IMAGE" && createTplHeaderPreview ? (
                                <img
                                  src={createTplHeaderPreview}
                                  alt=""
                                  style={{
                                    width: "calc(100% + 24px)",
                                    display: "block",
                                    objectFit: "contain",
                                    background: "#e9edef",
                                    borderRadius: "0 8px 0 0",
                                  }}
                                />
                              ) : createTplHeaderType === "VIDEO" && createTplHeaderPreview ? (
                                <video
                                  src={createTplHeaderPreview}
                                  controls
                                  playsInline
                                  preload="metadata"
                                  style={{
                                    width: "calc(100% + 24px)",
                                    display: "block",
                                    maxHeight: 220,
                                    objectFit: "contain",
                                    background: "#000",
                                    borderRadius: "0 8px 0 0",
                                  }}
                                />
                              ) : createTplHeaderType === "VIDEO" ? (
                                <div className="text-muted small mb-1">Video header — choose a file to preview</div>
                              ) : createTplHeaderType === "DOCUMENT" ? (
                                <div
                                  className="d-flex align-items-center gap-2 mb-2 p-2 rounded"
                                  style={{ background: "#f0f2f5", fontSize: 13 }}
                                >
                                  <span aria-hidden>📄</span>
                                  <span className="text-truncate">
                                    {createTplHeaderFile?.name || "Document header"}
                                  </span>
                                </div>
                              ) : createTplHeaderType === "LOCATION" ? (
                                <div className="text-muted small mb-1">Location header</div>
                              ) : createTplHeaderType === "NONE" && createTplHeader.trim() ? (
                                <div className="fw-bold mb-1" style={{ fontSize: 14 }}>
                                  {createTplHeader}
                                </div>
                              ) : null
                            }
                            belowBubbleNode={createTplButtons.map((btn, i) => {
                              const prevApi =
                                findOptionByValue(WA_TEMPLATE_BUTTON_TYPES, Number(btn.type))?.apiType ||
                                "";
                              return (
                              <div
                                key={`prev-btn-${i}`}
                                className="text-center"
                                style={{
                                  background: "#fff",
                                  borderRadius: 8,
                                  padding: "8px 12px",
                                  maxWidth: "92%",
                                  marginTop: 2,
                                  color: "#0d6efd",
                                  fontSize: 14,
                                  boxShadow: "0 1px 1px rgba(0,0,0,.12)",
                                }}
                              >
                                {prevApi === "COPY_CODE"
                                  ? `⧉ Copy code${btn.example ? ` (${btn.example})` : ""}`
                                  : prevApi === "URL"
                                    ? btn.text || "Visit link"
                                    : prevApi === "PHONE_NUMBER"
                                      ? btn.text || "Call"
                                      : btn.text || "Quick reply"}
                              </div>
                              );
                            })}
                          />
                        )}
                        {createTplCategory !== "AUTHENTICATION" && tplVarCount() > 0 ? (
                          <div
                            className={`small mt-2 ${
                              String(createTplExample || "")
                                .split(",")
                                .map((s) => s.trim())
                                .filter(Boolean).length === tplVarCount()
                                ? "text-success"
                                : "text-warning"
                            }`}
                          >
                            {tplVarCount()} variable(s) in body —{" "}
                            {String(createTplExample || "")
                              .split(",")
                              .map((s) => s.trim())
                              .filter(Boolean).length}{" "}
                            example value(s) given. Counts must match to submit.
                          </div>
                        ) : null}
                      </div>
                    </div>
                      </>
                    )}
                  </div>
                </div>
                )}
              </>
            )}

            {showBulk && (
              <div className="row g-4">
                <div className="col-lg-7">
                  <form id="wa-bulk-form" onSubmit={handleBulkSend}>
                  <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: "0.5rem" }}>
                    <div className="card-body">
                      <h2 className="h6 mb-3">
                        <span className="badge rounded-pill bg-primary me-2">1</span>
                        Who gets it
                      </h2>

                      {/* Audience picker — same idea as Push notification */}
                      <div className="row g-2 mb-3">
                        {[
                          { v: "contacts", t: "Contacts / numbers", d: "Pick or type numbers" },
                          { v: "subscribed", t: "Subscribed users", d: "All app users with a phone" },
                          { v: "segment", t: "Segment", d: "Users in a saved segment" },
                          { v: "test", t: "Test users", d: "Only test subscriptions" },
                        ].map((a) => (
                          <div className="col-6" key={a.v}>
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => setBulkAudience(a.v)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") setBulkAudience(a.v);
                              }}
                              className={`border rounded p-2 h-100 ${
                                bulkAudience === a.v ? "border-primary" : "border-light-subtle"
                              }`}
                              style={{
                                cursor: "pointer",
                                background: bulkAudience === a.v ? "rgba(67,70,206,.06)" : "#fff",
                                boxShadow: bulkAudience === a.v ? "0 0 0 1px #4346ce inset" : "none",
                              }}
                            >
                              <div className="fw-semibold small">{a.t}</div>
                              <div className="text-muted" style={{ fontSize: 12 }}>
                                {a.d}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {bulkAudience === "contacts" ? (
                        <>
                          <div className="d-flex justify-content-end mb-1">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => loadContactsForPicker()}
                              disabled={contactsLoading}
                            >
                              {contactsLoading ? "Loading…" : "Refresh contacts"}
                            </button>
                          </div>
                          <CreatableSelect
                            isMulti
                            closeMenuOnSelect={false}
                            isLoading={contactsLoading}
                            options={contactSelectOptions}
                            value={bulkRecipientSelect}
                            onChange={setBulkRecipientSelect}
                            placeholder={
                              contactsLoading ? "Loading contacts…" : "Select contacts or type any numbers…"
                            }
                            isValidNewOption={isValidManualPhone}
                            getNewOptionData={manualPhoneOption}
                            formatCreateLabel={formatCreateLabel}
                            noOptionsMessage={() =>
                              contactsLoading
                                ? "Loading…"
                                : "Type a full number with country code (no +), e.g. 9198XXXXXXXX"
                            }
                            styles={contactsSelectStyles}
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            menuPosition="fixed"
                          />
                          <div className="form-text">
                            WhatsApp contacts + app users, or type any number (country code, digits only, Enter).
                          </div>
                        </>
                      ) : bulkAudience === "segment" ? (
                        <>
                          <label className="form-label small mb-1">Choose segment(s)</label>
                          <Select
                            isMulti
                            options={bulkSegments.map((s) => ({ value: s.id, label: s.name }))}
                            value={bulkSegments
                              .filter((s) => bulkSelectedSegments.includes(s.id))
                              .map((s) => ({ value: s.id, label: s.name }))}
                            onChange={(opts) =>
                              setBulkSelectedSegments((opts || []).map((o) => o.value))
                            }
                            placeholder={bulkSegments.length ? "Select segments…" : "No segments found"}
                            styles={contactsSelectStyles}
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            menuPosition="fixed"
                          />
                          <div className="form-text">
                            {bulkAudienceCounting
                              ? "Counting reachable numbers…"
                              : bulkSelectedSegments.length === 0
                                ? "Select at least one segment."
                                : `${bulkAudienceCount ?? 0} reachable WhatsApp number(s) in selection.`}
                          </div>
                        </>
                      ) : (
                        <div className="alert alert-light border small mb-0" role="status">
                          {bulkAudienceCounting ? (
                            "Counting reachable numbers…"
                          ) : (
                            <>
                              This will send to{" "}
                              <strong>{bulkAudienceCount ?? 0}</strong>{" "}
                              {bulkAudience === "test" ? "test user(s)" : "subscribed user(s)"} who have a phone
                              number on file.
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: "0.5rem" }}>
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                        <h2 className="h6 mb-0">
                          <span className="badge rounded-pill bg-primary me-2">2</span>
                          What they receive
                        </h2>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => loadTemplates(true)}
                          disabled={templateListLoading}
                        >
                          {templateListLoading ? "Loading…" : "Refresh templates"}
                        </button>
                      </div>
                      <select
                        className="form-select"
                        value={
                          bulkTemplatePicker === "custom"
                            ? "custom"
                            : bulkTemplatePicker &&
                              approvedBulkTemplates.some(
                                (r) => bulkTplKey(r) === bulkTemplatePicker
                              )
                            ? bulkTemplatePicker
                            : ""
                        }
                        onChange={(ev) => {
                          const v = ev.target.value;
                          if (v === "") {
                            setBulkTemplatePicker("");
                            return;
                          }
                          if (v === "custom") {
                            setBulkTemplatePicker("custom");
                            return;
                          }
                          const row = approvedBulkTemplates.find((r) => bulkTplKey(r) === v);
                          if (row) {
                            setBulkTemplatePicker(v);
                            setBulkTemplateName(row.name);
                            setBulkLanguageCode(row.language || "en_US");
                            setBulkHeaderImageFile(null);
                            setBulkHeaderImageUrl("");
                            setBulkHeaderLat("");
                            setBulkHeaderLng("");
                          }
                        }}
                      >
                        <option value="">
                          {approvedBulkTemplates.length
                            ? "-- Select an approved template --"
                            : "-- No approved templates yet (refresh or approve in Meta) --"}
                        </option>
                        {approvedBulkTemplates.map((row) => (
                          <option key={bulkTplKey(row)} value={bulkTplKey(row)}>
                            {row.name} · {row.language}
                          </option>
                        ))}
                        <option value="custom">Other: type name &amp; language manually</option>
                      </select>
                      <div className="form-text mb-3">
                        {bulkTemplatePicker === "custom" ? (
                          <>
                            Sends immediately using an already-approved Meta template. Name and language must match Meta
                            exactly — this does not create a new template.
                          </>
                        ) : (
                          <>
                            Only <strong>APPROVED</strong> templates appear here. Pending or rejected templates are listed on the
                            Templates page until Meta approves them.
                          </>
                        )}
                      </div>
                    {bulkTemplatePicker === "custom" ? (
                      <div className="row g-3 mb-3">
                        <div className="col-md-6">
                          <label className="form-label">Template name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={bulkTemplateName}
                            onChange={(ev) => setBulkTemplateName(ev.target.value)}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Language code</label>
                          <input
                            type="text"
                            className="form-control"
                            value={bulkLanguageCode}
                            onChange={(ev) => setBulkLanguageCode(ev.target.value)}
                          />
                        </div>
                      </div>
                    ) : null}
                    {bulkTemplatePicker && (bulkVarCount() > 0 || bulkTemplatePicker === "custom") ? (
                      <div className="mb-3">
                        <label className="form-label">
                          Template variables
                          {bulkVarCount() > 0 ? (
                            <span className="text-muted small"> — this template needs {bulkVarCount()}</span>
                          ) : null}
                        </label>
                        <input
                          type="text"
                          className="form-control font-monospace"
                          value={bulkBodyParams}
                          onChange={(ev) => setBulkBodyParams(ev.target.value)}
                          placeholder="Comma-separated, in {{1}}, {{2}} order · OTP template = just the code"
                        />
                        {bulkVarCount() > 0 ? (
                          <div
                            className={`form-text ${
                              bulkVarValuesCount() === bulkVarCount() ? "text-success" : "text-warning"
                            }`}
                          >
                            {bulkVarValuesCount()} of {bulkVarCount()} value(s) filled — same values go to every
                            recipient.
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {bulkTemplatePicker &&
                    (bulkTemplatePicker === "custom" || templateHasDynamicUrlButton(bulkSelectedRow)) ? (
                      <div className="mb-3">
                        <label className="form-label">Button URL variable</label>
                        <input
                          type="text"
                          className="form-control font-monospace"
                          value={bulkButtonParam}
                          onChange={(ev) => setBulkButtonParam(ev.target.value)}
                          placeholder="Dynamic URL suffix (template button {{1}})"
                        />
                      </div>
                    ) : null}
                    {bulkTemplatePicker &&
                    (bulkTemplatePicker === "custom" || templateHasCopyCodeButton(bulkSelectedRow)) ? (
                      <div className="mb-3">
                        <label className="form-label">Copy offer code</label>
                        <input
                          type="text"
                          className="form-control font-monospace"
                          value={bulkCopyCodeParam}
                          onChange={(ev) => setBulkCopyCodeParam(ev.target.value)}
                          placeholder="Coupon/offer code to send"
                        />
                      </div>
                    ) : null}
                    {bulkTemplatePicker &&
                    (isFileHeaderFormat(templateHeaderFormat(bulkSelectedRow)) ||
                      templateHeaderFormat(bulkSelectedRow) === "LOCATION" ||
                      bulkTemplatePicker === "custom") ? (
                      <div className="mb-3 mt-3">
                        <label className="form-label">
                          Header media
                          {isFileHeaderFormat(templateHeaderFormat(bulkSelectedRow)) ? (
                            bulkHeaderCheck.status === "ok" ? (
                              <span className="text-muted small"> — using saved template media unless overridden below</span>
                            ) : bulkHeaderCheck.status === "checking" ? (
                              <span className="text-muted small"> — checking saved template media…</span>
                            ) : (
                              <span className="text-danger small"> — required for this template</span>
                            )
                          ) : null}
                        </label>
                        {isFileHeaderFormat(templateHeaderFormat(bulkSelectedRow)) &&
                          bulkHeaderCheck.status === "bad" &&
                          !bulkHeaderImageFile &&
                          !bulkHeaderImageUrl.trim() && (
                            <div className="text-danger small mb-2">{bulkHeaderCheck.reason}</div>
                        )}
                        {isFileHeaderFormat(templateHeaderFormat(bulkSelectedRow)) &&
                          bulkSelectedRow?.header_media_url &&
                          bulkHeaderCheck.status === "ok" &&
                          !bulkHeaderImageFile &&
                          !bulkHeaderImageUrl.trim() && (
                            <img
                              src={bulkSelectedRow.header_media_url}
                              alt="Saved template header"
                              className="mb-2"
                              style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 6, border: "1px solid #e5e7eb", display: "block" }}
                            />
                        )}
                        {templateHeaderFormat(bulkSelectedRow) === "LOCATION" ? (
                          <div className="row g-2">
                            <div className="col-md-6">
                              <input
                                type="text"
                                className="form-control"
                                value={bulkHeaderLat}
                                onChange={(ev) => setBulkHeaderLat(ev.target.value)}
                                placeholder="Latitude"
                              />
                            </div>
                            <div className="col-md-6">
                              <input
                                type="text"
                                className="form-control"
                                value={bulkHeaderLng}
                                onChange={(ev) => setBulkHeaderLng(ev.target.value)}
                                placeholder="Longitude"
                              />
                            </div>
                          </div>
                        ) : (
                          <>
                            <input
                              type="file"
                              className="form-control mb-2"
                              accept={headerFileAccept(templateHeaderFormat(bulkSelectedRow) || "IMAGE")}
                              onChange={(ev) =>
                                setBulkHeaderImageFile(
                                  ev.target.files && ev.target.files[0] ? ev.target.files[0] : null
                                )
                              }
                            />
                            <input
                              type="url"
                              className="form-control"
                              value={bulkHeaderImageUrl}
                              onChange={(ev) => setBulkHeaderImageUrl(ev.target.value)}
                              placeholder="Or paste a public HTTPS media URL"
                            />
                          </>
                        )}
                        <div className="form-text">
                          You can use different media on every send. The sample used when creating the template is not
                          locked in.
                        </div>
                      </div>
                    ) : null}
                    </div>
                  </div>
                  </form>

                  {bulkSummary && (
                    <div className="card border-0 shadow-sm" style={{ borderRadius: "0.5rem" }}>
                    <div className="card-body">
                      <h3 className="h6">Last run</h3>
                      {bulkSummary.queued && bulkSummary.campaignId ? (
                        <div className="small mb-2">
                          <p className="mb-2">
                            Queued <strong>{bulkSummary.total}</strong> message(s)
                            {bulkSummary.sendId ? (
                              <>
                                {" "}
                                · Send ID: <code>{bulkSummary.sendId}</code>
                              </>
                            ) : null}
                            {bulkSummary.delayMs != null ? (
                              <> (~{bulkSummary.delayMs}ms apart
                              {bulkSummary.estimatedMinutes != null
                                ? `, ~${bulkSummary.estimatedMinutes} min`
                                : ""}
                              )</>
                            ) : null}
                            .
                          </p>
                          <Link
                            className="btn btn-sm btn-outline-primary"
                            to={`/apps/${appId}/whatsapp/campaigns/${bulkSummary.campaignId}/report`}
                          >
                            Open delivery report
                          </Link>
                          <p className="text-muted mt-2 mb-0" style={{ fontSize: 12 }}>
                            Or WhatsApp → Campaigns for full send history.
                          </p>
                        </div>
                      ) : (
                        <>
                      <p className="small mb-2">
                        Sent: <strong>{bulkSummary.sent}</strong>, failed: <strong>{bulkSummary.failed}</strong>, delay{" "}
                        {bulkSummary.delayMs}ms
                      </p>
                      <div className="table-responsive" style={{ maxHeight: 280, overflow: "auto" }}>
                        <table className="table table-sm align-middle mb-0">
                          <thead>
                            <tr>
                              <th>To</th>
                              <th>Result</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(bulkSummary.results || []).map((r, i) => (
                              <tr key={`${r.to}-${i}`}>
                                <td className="font-monospace small">{r.to}</td>
                                <td className="small">
                                  {r.ok ? (
                                    <span className="text-success">Sent</span>
                                  ) : (
                                    <div>
                                      <span className="text-danger" title={r.error}>
                                        {r.error || "Error"}
                                      </span>
                                      {r.hint ? (
                                        <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                                          {r.hint}
                                        </div>
                                      ) : null}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                        </>
                      )}
                    </div>
                    </div>
                  )}
                </div>

                <div className="col-lg-5">
                  <div className="card border-0 shadow-sm" style={{ borderRadius: "0.5rem", position: "sticky", top: 16 }}>
                    <div className="card-body">
                      <div className="small fw-semibold text-muted mb-2">Message preview</div>
                      <div
                        style={{
                          background: "#e5ddd5",
                          borderRadius: 12,
                          padding: 16,
                          minHeight: 180,
                        }}
                      >
                        {bulkSelectedRow ? (
                          <div
                            style={{
                              background: "#fff",
                              borderRadius: "0 8px 8px 8px",
                              boxShadow: "0 1px 1px rgba(0,0,0,.12)",
                            }}
                          >
                            {bulkHeaderImagePreview || (bulkHeaderCheck.status === "ok" && bulkSelectedRow?.header_media_url && templateHeaderFormat(bulkSelectedRow) === "IMAGE" && !bulkHeaderImageUrl.trim()) ? (
                              <img
                                src={bulkHeaderImagePreview || bulkSelectedRow.header_media_url}
                                alt=""
                                style={{
                                  width: "100%",
                                  margin: "0 0 10px",
                                  objectFit: "cover",
                                  borderRadius: "0 8px 0 0",
                                  display: "block",
                                }}
                              />
                            ) : null}
                            <div
                              className="wa-md"
                              style={{ whiteSpace: "pre-wrap", fontSize: 14, padding: "8px 12px" }}
                              dangerouslySetInnerHTML={{
                                __html: formatWhatsAppMarkdownHtml(bulkPreviewBody()),
                              }}
                            />
                            <div className="text-end text-muted" style={{ fontSize: 11, marginTop: 4, padding: "8px 12px" }}>
                              {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </div>
                          </div>
                        ) : (
                          <div className="text-muted small text-center pt-4">
                            {bulkTemplatePicker === "custom"
                              ? "Custom template — preview not available"
                              : "Select a template to see what customers will receive"}
                          </div>
                        )}
                      </div>

                      {(() => {
                        const audienceCountResolved =
                          bulkAudience === "contacts" ? bulkRecipientSelect.length : bulkAudienceCount || 0;
                        const audienceLabel =
                          bulkAudience === "contacts"
                            ? "recipient(s)"
                            : bulkAudience === "subscribed"
                              ? "subscribed user(s)"
                              : bulkAudience === "test"
                                ? "test user(s)"
                                : "user(s) in segment(s)";
                        const varsOk = !(bulkVarCount() > 0 && bulkVarValuesCount() !== bulkVarCount());
                        const templateOk = bulkSelectedRow || bulkTemplatePicker === "custom";
                        const fmt = templateHeaderFormat(bulkSelectedRow);
                        const headerImageOk = !(
                          (isFileHeaderFormat(fmt) && !bulkHeaderImageFile && !bulkHeaderImageUrl.trim() && bulkHeaderCheck.status !== "ok") ||
                          (fmt === "LOCATION" && (!bulkHeaderLat.trim() || !bulkHeaderLng.trim()))
                        );
                        const sendDisabled =
                          bulkSending || audienceCountResolved === 0 || !templateOk || !varsOk || !headerImageOk;
                        return (
                          <>
                            <div className="d-flex justify-content-between align-items-center border-top mt-3 pt-3">
                              <div className="small">
                                <div>
                                  <strong>{audienceCountResolved}</strong> {audienceLabel}
                                </div>
                                <div className="text-muted">
                                  {bulkSelectedRow
                                    ? `${bulkSelectedRow.name} · ${bulkSelectedRow.language}`
                                    : bulkTemplatePicker === "custom"
                                      ? `${bulkTemplateName || "—"} · ${bulkLanguageCode || "—"}`
                                      : "No template selected"}
                                </div>
                              </div>
                              {can("whatsapp.send.manage") ? (
                              <button
                                type="submit"
                                form="wa-bulk-form"
                                className="add_app_btn btn_theme_primary"
                                disabled={sendDisabled}
                              >
                                {bulkSending
                                  ? bulkHeaderImageFile
                                    ? "Uploading & sending…"
                                    : "Sending…"
                                  : `Send to ${audienceCountResolved}`}
                              </button>
                              ) : null}
                            </div>
                            {!varsOk ? (
                              <div className="small text-warning mt-2">
                                Fill all {bulkVarCount()} template variable(s) to enable send.
                              </div>
                            ) : null}
                          </>
                        );
                      })()}
                      <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                        Messages go out one by one (~350ms apart) in a background campaign so every send (1 or many) has a delivery report under WhatsApp → Campaigns.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </>
        ) : null}
      </div>
    </div>
  );
};

export default WhatsAppSettings;
